import PricingBreakdown from '@/components/PricingBreakdown';
import PricingDrawer from '@/components/PricingDrawer';
import { EmbedStrip, WordingQuickForm } from '@/components/EmbedFocus';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, MapPin, MapPinned, RefreshCw, AlertCircle, User, DollarSign, Plus, Trash2, Save, Calendar, FileText, Mail, Smartphone, Package, Home, Edit2, X, ClipboardList, ChevronLeft, ChevronRight, Lock, ChevronDown, Send, Building2, HardHat, CheckCircle2, ExternalLink } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import AppraisalComponentsTab from '@/components/AppraisalComponentsTab';
import PhotoGallery from '@/pages/PhotoGallery';
import EmailModal from '@/pages/EmailModal';
import CulturalToolkitCard from '@/components/CulturalToolkitCard';
import CatalogPicker from '@/components/CatalogPicker';
import RoofForm from '@/components/RoofForm';
import Combobox from '@/components/Combobox';
import QuoteTemplatePicker from '@/components/QuoteTemplatePicker';
import ProductAutoSuggest from '@/components/ProductAutoSuggest';
import ExportsPanel from '@/components/ExportsPanel';
import SendScriptModal from '@/components/SendScriptModal';
import CommunicationsLogPanel from '@/components/CommunicationsLogPanel';
import SentQuotesPanel from '@/components/SentQuotesPanel';
import CompletionCertificateCard from '@/components/CompletionCertificateCard';
import JobTrackingSheetCard from '@/components/JobTrackingSheetCard';
import QualitySignoffCard from '@/components/QualitySignoffCard';
import ConditionScriptPicker from '@/components/ConditionScriptPicker';
import InspectionCommentsPanel from '@/components/InspectionCommentsPanel';
import RoofAppraisalTab from '@/components/RoofAppraisalTab';
import RoofDesignTemplatePicker from '@/components/RoofDesignTemplatePicker';
import UndoToolbar from '@/components/UndoToolbar';
import ExtraWorkEditor from '@/components/ExtraWorkEditor';
import RentalStatusInline from '@/components/RentalStatusInline';
import EnvironmentZoneCard from '@/components/EnvironmentZoneCard';
import AppraisalTimeline from '@/components/AppraisalTimeline';
import { promptStageBump } from '@/lib/stageBumpPrompt';
import RoofDiagramAnnotator from '@/components/RoofDiagramAnnotator';
import { ChildQuoteBanner } from '@/components/ChildQuoteBanner';
import { QuotationTypeSelect } from '@/components/QuotationTypeSelect';
import ContractorSheetPicker from '@/components/ContractorSheetPicker';
import RoofDiagramPickerInline from '@/components/RoofDiagramPickerInline';
import RoofDiagramSlotsCard from '@/components/RoofDiagramSlotsCard';
import SlotAnnotatorModal from '@/components/SlotAnnotatorModal';
import StatusConfirmDialog from '@/components/StatusConfirmDialog';
import { useStages, stageLabel } from '@/lib/stages';
import PushDiagramToComponentsButton from '@/components/PushDiagramToComponentsButton';
import SaveDocButton from '@/components/SaveDocButton';
import { OpenInDriveButton } from '@/components/drive/OpenInDriveButton';
import { QuotationTypeBar } from '@/components/QuotationTypeBar';
import { JobQuotesPanel } from '@/components/JobQuotesPanel';
import EmailScriptModal from '@/components/EmailScriptModal';
import ReadyToQuoteBanner from '@/components/ReadyToQuoteBanner';
import { OnlineAcceptanceCard } from '@/components/OnlineAcceptanceCard';
import PropertyTenantAgentCard from '@/components/PropertyTenantAgentCard';
import ReroofBuilderTab from '@/components/ReroofBuilderTab';
import RepairsBuilderTab from '@/components/RepairsBuilderTab';
import RemedialRepairsBuilderTab from '@/components/RemedialRepairsBuilderTab';
import RestorationBuilderTab from '@/components/RestorationBuilderTab';
import PaintingBuilderTab from '@/components/PaintingBuilderTab';
import MaintenanceBuilderTab from '@/components/MaintenanceBuilderTab';
import GutteringBuilderTab from '@/components/GutteringBuilderTab';
import { DraftBuilderGate } from '@/components/DraftBuilderGate';
import { DraftsReminderBanner } from '@/components/DraftsReminderBanner';
import RoofDetailsQuickBar from '@/components/RoofDetailsQuickBar';
import AttachmentsTab from '@/components/AttachmentsTab';
import WorkCompletionTab from '@/components/WorkCompletionTab';
import ContractorOverviewTab from '@/components/ContractorOverviewTab';
import MaterialsTab from '@/components/MaterialsTab';
import JobExecutionPanel from '@/pages/JobExecutionPanel';
import PendingMaterialsCard from '@/components/PendingMaterialsCard';
import CompareQuotesDialog from '@/components/CompareQuotesDialog';
import { statusPillClasses, statusDotColour } from '@/lib/status';
import { fireRoofDetailsChanged } from '@/utils/roofEvents';
import PropertyLinkBanner from '@/components/PropertyLinkBanner';
import AppraisalContextStrip from '@/components/AppraisalContextStrip';
import IdChip from '@/components/IdChip';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import AppraisalTabsCustomiser, { useAppraisalTabsConfig, buildTabList } from '@/components/AppraisalTabsCustomiser';
import BookingCard from '@/components/BookingCard';
import { Settings2 } from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Overview', 'Roof Appraisal', 'Booking', 'Diagrams', 'Builder', 'Components', 'Roof Details', 'Quote Calculation', 'Quote Items', 'Contractors', 'Materials', 'Job Tasks', 'Photos', 'Work Sheets', 'Quality', 'Attachments', 'Work Completion', 'Exports'];

// The Builder tab hosts 4 sub-tabs (Re-roof / Repairs / Restoration /
// Maintenance). The appraisal's service_option determines which sub-tab
// opens by default, but all four are always reachable so the operator
// can spec multiple service types on a single appraisal.
const childBuilderOf = (d) => (d?.parent_appraisal_id ? (d.builder_origin || d.draft_builder || '') : '');
const subtabsFor = (d) => (childBuilderOf(d) ? BUILDER_SUBTABS.filter(b => b.key === childBuilderOf(d)) : BUILDER_SUBTABS);
const BUILDER_SUBTABS = [
  { key: 'reroof',           label: 'Re-roof' },
  // Iter157 Phase A — "Remedial Repairs" is a new lightweight builder
  // for localised leak-area repairs. "Repairs" is the existing full-
  // scope builder. Iter158 Phase B — added "Painting" as a standalone
  // builder; the existing "Restoration" tab is now the Combiner that
  // bundles Repairs + Paint with discount logic.
  { key: 'remedial_repairs', label: 'Remedial Repairs' },
  { key: 'repairs',          label: 'Repairs' },
  { key: 'painting',         label: 'Recoat' },
  { key: 'restoration',      label: 'Restoration' },
  { key: 'maintenance',      label: 'Maintenance' },
  { key: 'guttering',        label: 'Guttering' },
];
const SERVICE_TO_SUBTAB = {
  'Reroof':           'reroof',
  'New Roof':         'reroof',
  'Remedial Repairs': 'remedial_repairs',
  'Repairs':          'repairs',
  'Painting':         'painting',
  'Restoration':      'restoration',
  'Maintenance':      'maintenance',
};

// Iter211 — map a quote_type_ref string ("Rescrew and Repair Longrun",
// "Concrete Restoration", etc.) to the matching Builder sub-tab. Drives
// pre-selection so the operator lands on the right builder even when
// the broader `service_option` is the legacy default. Keyword-based so
// it survives small wording variations in the quote_type catalogue.
const QUOTE_TYPE_REF_TO_SUBTAB = (ref) => {
  const s = (ref || '').toLowerCase();
  if (!s) return null;
  // Order matters — most-specific first. "Restoration" wins over "Repair"
  // because some restoration scenarios include "+ Repair" in their label.
  if (/re[-\s]*coat/.test(s))                         return 'painting';
  if (s.includes('restoration') || s.includes('paint')) return 'restoration';
  if (s.includes('remedial'))                           return 'remedial_repairs';
  if (s.includes('repair') || s.includes('rescrew'))    return 'repairs';
  if (s.includes('maintenance'))                        return 'maintenance';
  if (s.includes('reroof') || s.includes('new roof'))   return 'reroof';
  return null;
};

// SERVICE_BUILDER_LABEL retired in iter74 — the Builder tab now hosts
// every service option as visible sub-tabs (see BUILDER_SUBTABS above).


// Roof material classifier. Walks every roof on the appraisal and
// tallies tile vs metal based on the `roof_type` / `roof_profile`
// strings. Returns one of:
//   - 'tile'   — every roof is a tile-family system
//   - 'metal'  — every roof is a metal-family system
//   - 'mixed'  — both classes present (rare but real: lean-to extensions)
//   - null     — no roofs OR none can be classified yet
// The same regex (`tile|concrete|decramastic|clay`) drives the Roof
// Diagram Annotator's Dektite-variant default, so a roof flagged
// "tile" here will also auto-pick Lead aquaseals when the operator
// drops pipe pins.
function classifyRoofMaterial(roofs) {
  if (!Array.isArray(roofs) || roofs.length === 0) return null;
  const tileRe = /tile|concrete|decramastic|clay|cement/i;
  const metalRe = /metal|long[\s-]*run|colorsteel|coloursteel|gerard|press[\s-]*formed|press[\s-]*metal/i;
  let tile = 0, metal = 0;
  for (const r of roofs) {
    const blob = [r.roof_type, r.roof_profile, r.roof_brand].filter(Boolean).join(' ');
    if (!blob.trim()) continue;
    if (tileRe.test(blob)) tile += 1;
    else if (metalRe.test(blob)) metal += 1;
    else metal += 1;  // assume metal for un-flagged profiles (Corrugate / TrimRib / etc.)
  }
  if (tile > 0 && metal === 0) return 'tile';
  if (metal > 0 && tile === 0) return 'metal';
  if (tile > 0 && metal > 0) return 'mixed';
  return null;
}

export default function AppraisalDetailPage() {
  const { api, user } = useAuth();
  const stages = useStages(api);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  // Honour ?tab=… deep-link (used by the Documents Search "Open" jump
  // button so it lands directly on the Attachments tab).
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const q = searchParams.get('tab');
    return TABS.includes(q) ? q : 'Overview';
  })();
  const [tab, setTab] = useState(initialTab);
  // iter303ak-fix4 · Embed mode. When ?embed=roof_appraisal is in the URL,
  // the Runner opened this page as a popup with the sole intent of
  // filling in the Roof Appraisal tab. In embed mode we:
  //   • hide sidebar / top-nav / Back-to-Appraisals / timeline / property
  //     banner / context strip / booking card / tab strip / all other tabs
  //   • force `tab='Roof Appraisal'` regardless of what's saved in state
  //   • show a slim top strip with A#/C#/P# + a "Save & Return to Runner"
  //     button that persists via the tab's Save handler and then
  //     window.close()s the popup so the Runner's poller advances.
  const embedMode = searchParams.get('embed') || '';
  const isEmbedRoofAppraisal = embedMode === 'roof_appraisal';
  const isEmbedQuoteBuilder = embedMode === 'quote_builder';
  // iter303ak-fix16 · Annotator embed. Opened from a builder embed
  // popout via the "Open Master Diagram" button so the operator can
  // edit pins without leaving the Quote Builder popout tab. On close
  // via Save, the parent Builder popout auto-refreshes + shows an undo
  // toast with the diff.
  const isEmbedAnnotator = embedMode === 'annotator';
  // iter303ak-fix24 · Accepted T30 embed modes. Runner popouts to
  // review the child's Components / Contractors data respectively.
  const isEmbedComponents = embedMode === 'components';
  const isEmbedContractors = embedMode === 'contractors';
  // iter303ak-fix27 · T50 embed of the Materials tab.
  const isEmbedMaterials = embedMode === 'materials';
  // iter303ak-fix29 · Work In Progress T50 · Job Execution embed.
  const isEmbedJobExecution = embedMode === 'job-execution';
  // Update & Resend focused pop-outs: pricing drawer only / wording fields only.
  const isEmbedPricing = embedMode === 'pricing';
  const isEmbedWording = embedMode === 'wording';
  const wordingSaveRef = useRef(null);
  // Builder sub-tab (Re-roof / Repairs / Restoration / Maintenance) —
  // defaults to whichever sub-tab matches the appraisal's
  // service_option. Honour ?builder=… deep-link so attachments / docs
  // can route operators straight to e.g. the Maintenance sub-tab.
  const initialBuilderSubtab = (() => {
    const q = searchParams.get('builder');
    return BUILDER_SUBTABS.some((b) => b.key === q) ? q : null;
  })();
  const [builderSubtab, setBuilderSubtab] = useState(initialBuilderSubtab);

  // iter303ak-fix16 · Annotator diff-toast state (only relevant when
  // this component is rendering inside `?embed=quote_builder`). The
  // parent snapshot the current `roof_diagram_annotations` before
  // opening the annotator popup; on window.close it diffs the fresh
  // vs snapshot and shows an undo toast so the operator can revert
  // if the pin edits weren't wanted.
  const annotatorSnapshotRef = useRef(null);
  const annotatorWindowRef = useRef(null);
  const annotatorPollerRef = useRef(null);
  const [annotatorDiffToast, setAnnotatorDiffToast] = useState(null);
  const openAnnotatorPopout = useCallback(() => {
    if (!id) return;
    // Snapshot current pins (deep-cloned) so Undo can PATCH them back
    // even after loadData() refreshes `data`.
    try {
      annotatorSnapshotRef.current = JSON.parse(JSON.stringify(data?.roof_diagram_annotations || []));
    } catch { annotatorSnapshotRef.current = []; }
    const url = `/appraisals/${id}?embed=annotator&slot=roof_plan`;
    const win = window.open(url, `annotator_${id}`, 'width=1400,height=900,noopener=no');
    if (!win) { alert('Annotator popup blocked — check your browser popup settings.'); return; }
    annotatorWindowRef.current = win;
    // Poll for popup close (mirrors GotoForm.js pattern).
    if (annotatorPollerRef.current) clearInterval(annotatorPollerRef.current);
    annotatorPollerRef.current = setInterval(async () => {
      if (win.closed) {
        clearInterval(annotatorPollerRef.current);
        annotatorPollerRef.current = null;
        annotatorWindowRef.current = null;
        // Refresh appraisal data
        try {
          const { data: fresh } = await api.get(`/appraisals/${id}`);
          const beforeCount = (annotatorSnapshotRef.current || []).length;
          const afterCount = (fresh?.roof_diagram_annotations || []).length;
          const added = Math.max(0, afterCount - beforeCount);
          const removed = Math.max(0, beforeCount - afterCount);
          const stayed = Math.min(beforeCount, afterCount);
          let summary;
          if (added === 0 && removed === 0) {
            summary = `${stayed} pin${stayed === 1 ? '' : 's'} refreshed — no net change.`;
          } else {
            const parts = [];
            if (added) parts.push(`${added} pin${added === 1 ? '' : 's'} added`);
            if (removed) parts.push(`${removed} pin${removed === 1 ? '' : 's'} removed`);
            parts.push(`${afterCount} total`);
            summary = parts.join(' · ');
          }
          setData(fresh);
          setBuilderRefreshKey(k => k + 1);  // v10.18 · nudge ReroofBuilderTab to re-fetch dims+specs
          setAnnotatorDiffToast({ summary, before: annotatorSnapshotRef.current });
          // v10.24 (Feb 2026 · Go-Live prep · Bug 2 Answer A) — Auto-emit
          // of pins → component_lines on annotator close is DISABLED. At
          // go-live every quote is built deliberately through the
          // builder tabs; pins carry measurements only. The operator
          // can still trigger a Pull manually via the "Pull from
          // Diagram" button on the Components tab. Historical auto-fire
          // site kept as a commented breadcrumb in case Trevor's team
          // ever wants it back behind a feature flag.
          //   pullFromDiagram({ silent: true });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('annotator refresh failed:', err);
        }
      }
    }, 500);
  }, [id, data, api]);
  const undoAnnotatorChanges = useCallback(async () => {
    if (!annotatorDiffToast) return;
    try {
      await api.patch(`/appraisals/${id}/roof-diagram-annotations`, { annotations: annotatorDiffToast.before || [] });
      // Refresh so the builder tabs re-render with the reverted pins.
      const { data: fresh } = await api.get(`/appraisals/${id}`);
      setData(fresh);
      setAnnotatorDiffToast(null);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Undo failed');
    }
  }, [annotatorDiffToast, api, id]);
  // iter303ak-fix18 · Diagram → Builder auto-sync. Push-to-components
  // is fired (a) on annotator popup close, (b) on builder embed mount,
  // and (c) via the manual "Pull from Diagram" button in the embed
  // strip. `pullDiffToast` shows a full component-type diff summary
  // after each pull. `componentsEmpty` drives the proactive empty-state
  // banner shown when the builder loads with zero component_lines.
  const [pullDiffToast, setPullDiffToast] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [componentsEmpty, setComponentsEmpty] = useState(false);
  const componentsMountedRef = useRef(false);
  const formatRollup = useCallback((rollup, linesCreated) => {
    // rollup = { "Sheet": {count, length}, "Ridge": {count, length}, ... }
    if (!rollup || Object.keys(rollup).length === 0) {
      return linesCreated ? `${linesCreated} line${linesCreated === 1 ? '' : 's'} refreshed.` : 'No changes.';
    }
    const parts = Object.entries(rollup)
      .filter(([, v]) => (v?.count || 0) > 0)
      .map(([name, v]) => `${v.count} ${name.toLowerCase()}${v.count === 1 ? '' : 's'}`);
    return parts.length ? parts.join(' · ') : `${linesCreated || 0} components refreshed.`;
  }, []);
  const pullFromDiagram = useCallback(async ({ silent = false } = {}) => {
    if (!id) return;
    if (!silent) setPulling(true);
    try {
      const { data: r } = await api.post(`/appraisals/${id}/roof-diagram-annotations/push-to-components`);
      const summary = formatRollup(r?.rollup, r?.lines_created);
      const total = r?.lines_created || Object.values(r?.rollup || {}).reduce((s, v) => s + (v?.count || 0), 0);
      // Refresh the component_lines count so the empty-state banner
      // disappears if pins landed successfully.
      try {
        const { data: comps } = await api.get(`/appraisals/${id}/components`);
        setComponentsEmpty(!(Array.isArray(comps?.items) ? comps.items.length : (Array.isArray(comps) ? comps.length : 0)));
      } catch { /* non-blocking */ }
      if (!silent) {
        setPullDiffToast({
          summary,
          isNoop: !total,
          at: Date.now(),
        });
        // Auto-dismiss after 6s so the strip doesn't stay cluttered.
        setTimeout(() => setPullDiffToast(t => (t && Date.now() - t.at >= 5900 ? null : t)), 6000);
      }
    } catch (e) {
      if (!silent) {
        setPullDiffToast({
          summary: e?.response?.data?.detail || 'Pull failed — check the Master Diagram has pins',
          isError: true,
          at: Date.now(),
        });
      }
    } finally {
      if (!silent) setPulling(false);
    }
  }, [id, api, formatRollup]);
  // v10.24 (Feb 2026 · Go-Live prep · Bug 2 Answer A) — Auto-pull on
  // builder embed mount is DISABLED. At go-live children arrive at $0
  // and the operator prices every quote deliberately through the
  // builder tabs. We still keep the empty-state banner + manual "Pull
  // from Diagram" button. The old auto-fire is preserved as a comment
  // in case Trevor's team ever wants it back behind a feature flag.
  useEffect(() => {
    if (!isEmbedQuoteBuilder || !id || componentsMountedRef.current) return;
    componentsMountedRef.current = true;
    (async () => {
      // First check whether components already exist so we know if the
      // empty-state banner should fire.
      try {
        const { data: comps } = await api.get(`/appraisals/${id}/components`);
        const n = Array.isArray(comps?.items) ? comps.items.length : (Array.isArray(comps) ? comps.length : 0);
        setComponentsEmpty(n === 0);
      } catch { /* ignore */ }
      // Historical auto-pull disabled for go-live:
      //   pullFromDiagram({ silent: false });
    })();
  }, [isEmbedQuoteBuilder, id, api, pullFromDiagram]);
  // Clean up poller if component unmounts mid-open.
  useEffect(() => () => {
    if (annotatorPollerRef.current) clearInterval(annotatorPollerRef.current);
  }, []);

  // Iter182++ — operator-customisable tab order + labels (persists per
  // browser via localStorage). The original tab KEYS are unchanged so
  // every `tab === 'Foo'` dispatch below still works regardless of
  // operator relabel. Hidden tabs are filtered out of the strip.
  const tabsCfg = useAppraisalTabsConfig(TABS);
  const [tabsCustomiserOpen, setTabsCustomiserOpen] = useState(false);
  // Iter229z — bumps when the annotator's "Push to Components" button
  // succeeds, so the AppraisalComponentsTab below re-fetches without
  // the operator having to switch tabs.
  const [componentsRefreshKey, setComponentsRefreshKey] = useState(0);
  // v10.18 (Feb 2026 · UAT A999577) · Master-Diagram → Reroof builder
  // auto-refresh. Bumped when the annotator popout closes so the
  // embedded ReroofBuilderTab re-fetches dims + specs and its
  // Option-A/B/C tiles show the operator's fresh measurements
  // without needing them to click "Sync all from live dims".
  const [builderRefreshKey, setBuilderRefreshKey] = useState(0);
  const visibleTabs = useMemo(
    () => buildTabList(tabsCfg, TABS).filter((t) => !t.hidden),
    [tabsCfg.order, tabsCfg.labels, tabsCfg.hidden],   // eslint-disable-line react-hooks/exhaustive-deps
  );

  const switchTab = (t) => {
    setTab(t);
    if (t === 'Quote Items') {
      api.get(`/appraisal-components/${id}`).then(r => setComponents(r.data || [])).catch(() => {});
    }
  };
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  // Wave 2 · H9 fix — snapshot of the appraisal as loaded from the server.
  // `saveAppraisal` diffs `form` against this ref so we only PUT fields the
  // operator actually edited. Prevents stale defaults (e.g. blank
  // `roof_subtype`) from reverting values set by other workflows between
  // load and save. See test_wave2_h9_subtype_revert.py.
  const originalRef = useRef({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [roofDetails, setRoofDetails] = useState([]);
  const [newRoof, setNewRoof] = useState(null);
  const [editRoof, setEditRoof] = useState(null);
  const [components, setComponents] = useState([]);
  const [checkedComps, setCheckedComps] = useState({});
  const [itemDescriptions, setItemDescriptions] = useState({});
  const [itemRequirements, setItemRequirements] = useState({});
  // Wave 2.2 (2026-02) — `cloning` state deleted along with the
  // "Convert to Quote" clone workflow. The status pill row handles the
  // "Quoted" transition in place, and the clone endpoint's
  // `source_appraisal_id` pointer was never read anywhere.
  const [dropdowns, setDropdowns] = useState({});
  const [showSendScript, setShowSendScript] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [commsRefreshKey, setCommsRefreshKey] = useState(0);
  const [showConditionPicker, setShowConditionPicker] = useState(false);
  // Roof Details Quick-Edit Bar preview overrides — when the operator
  // edits Roof Type / Brand / Profile in the builder header bar, the
  // canonicalised subtype + tile overrides flow through here so the
  // Restoration builder (which consumes `appraisal.roof_subtype` from
  // its prop) hot-swaps live without persisting. Set to null when no
  // preview is active.
  const [roofPreview, setRoofPreview] = useState(null);

  // Iter215 — annotator pop-out modal. Lets the operator open the full
  // roof-plan annotator directly from a builder Component Header (Ridge
  // / Barge / Valley / Sheet / Flashing / Pipe / Flue) so they can edit
  // pins without leaving their place in the Repairs builder. On close
  // the page reloads so the builder pin rows refresh with the edits.
  // `pinTypeHint` is purely cosmetic — the modal renders the full
  // canvas; it's surfaced in the modal title.
  const [annotatorPopOut, setAnnotatorPopOut] = useState(null); // {pinTypeHint, from}

  // Iter303 — Inline Quote-Type editor at the header. State only here;
  // save handler is defined below once `loadData` is available.
  const [editingQuoteType, setEditingQuoteType] = useState(false);
  const [quoteTypeSaving, setQuoteTypeSaving] = useState(false);
  // Iter303c — Grouped list ({label, refs[]}[]) sorted the same way
  // Manage/Quote Types orders rows by Category. Renders as <optgroup>s
  // in the header picker for at-a-glance section headings.
  const [quoteTypesGrouped, setQuoteTypesGrouped] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [appRes, sumRes] = await Promise.all([
        api.get(`/appraisals/${id}`),
        api.get(`/quote-summary/${id}`).catch(() => ({ data: null }))
      ]);
      setData(appRes.data);
      setSummary(sumRes.data);
      setForm(appRes.data);
      // Wave 2 · H9 — capture the pristine server state so `saveAppraisal`
      // can send only the fields the operator actually changed.
      originalRef.current = appRes.data || {};
      // Load roof details for the property
      if (appRes.data?.property_id) {
        try {
          const propRes = await api.get(`/properties/${appRes.data.property_id}`);
          setRoofDetails(propRes.data.roof_details || []);
        } catch {}
      }
      // Load components for summary
      try {
        const compRes = await api.get(`/appraisal-components/${id}`);
        setComponents(compRes.data || []);
      } catch {}
    } catch { navigate('/appraisals'); }
    setLoading(false);
  }, [api, id, navigate]);

  const load = loadData;

  useEffect(() => { load(); }, [load]);

  // Iter303 — inline Quote-Type save (silent PATCH; server also
  // back-syncs `service_option` from the quote_type_ref keywords).
  const saveQuoteTypeInline = async (newRef) => {
    if ((newRef || '') === (data?.quote_type_ref || '')) { setEditingQuoteType(false); return; }
    try {
      setQuoteTypeSaving(true);
      await api.patch(`/appraisals/${id}/quote-type`, { quote_type_ref: newRef });
      setEditingQuoteType(false);
      window.dispatchEvent(new Event('rtr:quote-type-changed'));
      await loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change Quotation Type');
    } finally {
      setQuoteTypeSaving(false);
    }
  };

  // Iter303 — honour `?edit=quote_type` deep-link (used by the Property
  // sidebar pencil) so operators land straight in the inline editor.
  useEffect(() => {
    if (!data) return;
    if (searchParams.get('edit') === 'quote_type') {
      setEditingQuoteType(true);
    }
  }, [data, searchParams]);

  // Pricing history for the persistent Undo feature. Loaded from MongoDB
  // on mount and re-fetched after every PATCH so undo survives reloads and
  // shows what other users have changed.
  const [pricingHistory, setPricingHistory] = useState([]);
  // Iter177 — soft/hard delete dialog visibility for this appraisal.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const refreshPricingHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/appraisals/${id}/pricing-history`);
      setPricingHistory(Array.isArray(data) ? data : []);
    } catch (e) { /* non-fatal */ }
  }, [api, id]);
  useEffect(() => { refreshPricingHistory(); }, [refreshPricingHistory]);

  const undoPricing = useCallback(async () => {
    if (pricingHistory.length === 0) return;
    const last = pricingHistory[0]; // newest first
    try {
      const { data: nextSummary } = await api.post(
        `/appraisals/${id}/pricing-history/${last.id}/restore`,
      );
      setSummary(nextSummary);
      setData(p => p ? { ...p,
        final_quote_markup: last.markup,
        discounts: last.discount_keys,
        extra_discount_amount: last.extra_discount_amount,
        contract_amount: Math.round(Number(nextSummary.grand_total || 0) * 100) / 100,
      } : p);
      setForm(p => ({ ...p,
        final_quote_markup: last.markup,
        discounts: last.discount_keys,
        extra_discount_amount: last.extra_discount_amount,
        contract_amount: Math.round(Number(nextSummary.grand_total || 0) * 100) / 100,
      }));
      await refreshPricingHistory();
    } catch (e) { console.error('undoPricing failed', e); }
  }, [api, id, pricingHistory, refreshPricingHistory]);

  // Inline edit helper used by the Quote Summary panel — patches only the
  // pricing fields and immediately refreshes the summary state. Also keeps
  // the parent `data` in sync so the Details card's Contract Amount mirror
  // updates without a full reload. The backend automatically snapshots the
  // BEFORE state into `pricing_history` so we just re-fetch it afterwards.
  const patchPricing = useCallback(async (patch) => {
    try {
      const { data: nextSummary } = await api.patch(`/appraisals/${id}/pricing`, patch);
      setSummary(nextSummary);
      setData(prev => prev ? {
        ...prev,
        final_quote_markup: patch.final_quote_markup ?? prev.final_quote_markup,
        discounts: patch.discounts ?? prev.discounts,
        extra_discount_amount: patch.extra_discount_amount ?? prev.extra_discount_amount,
        contract_amount: Math.round(Number(nextSummary.grand_total || 0) * 100) / 100,
      } : prev);
      setForm(prev => ({
        ...prev,
        final_quote_markup: patch.final_quote_markup ?? prev.final_quote_markup,
        discounts: patch.discounts ?? prev.discounts,
        extra_discount_amount: patch.extra_discount_amount ?? prev.extra_discount_amount,
        contract_amount: Math.round(Number(nextSummary.grand_total || 0) * 100) / 100,
      }));
      // Re-pull the persisted history so the Undo button knows about the
      // snapshot the backend just took.
      refreshPricingHistory();
    } catch (e) {
      console.error('patchPricing failed', e);
    }
  }, [api, id, refreshPricingHistory]);

  // Load item descriptions and requirements for dropdowns
  useEffect(() => {
    api.get('/item-descriptions/by-category').then(r => {
      const map = {};
      r.data.forEach(g => { map[g.category] = g.descriptions; });
      setItemDescriptions(map);
    }).catch(() => {});
  }, [api]);

  // Load requirements filtered by property roof types
  useEffect(() => {
    const roofType = roofDetails.length > 0 ? roofDetails[0].roof_type : '';
    const url = roofType ? `/item-requirements/by-category?roof_type=${roofType}` : '/item-requirements/by-category';
    api.get(url).then(r => {
      const map = {};
      r.data.forEach(g => { map[g.category] = g.requirements; });
      setItemRequirements(map);
    }).catch(() => {});
  }, [api, roofDetails]);

  // Load dropdown options for forms
  useEffect(() => {
    api.get('/dropdowns').then(r => setDropdowns(r.data || {})).catch(() => {});
    // Iter303c — grouped-by-category list for the header quote-type
    // picker's optgroup labels. Falls back to a single flat group if
    // the endpoint is unavailable so the picker still renders.
    api.get('/dropdowns/quote_types/grouped')
      .then(r => setQuoteTypesGrouped(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuoteTypesGrouped([]));
  }, [api]);

  // Contractors for booking
  const [contractors, setContractors] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  // Email-Quote-to-Client modal flag — re-uses the existing EmailModal with
  // attach_pdf pre-ticked + pdf_type='quotation'. Falls back to the email
  // log when SMTP isn't configured (existing send_email behaviour).
  const [showEmailQuoteModal, setShowEmailQuoteModal] = useState(false);
  // Go-Live Batch S · Quote Package modal — attach_pdf=quote-package
  // (merged Cover Letter + Company Overview + Quotation Letter PDF).
  const [showSendPackageModal, setShowSendPackageModal] = useState(false);
  // 2026-02 · Copy Email Script v1 — single read-only modal shown
  // from the toolbar; auto-picks the right script by appraisal_status.
  const [showEmailScriptModal, setShowEmailScriptModal] = useState(false);
  // Go-Live Batch S · build confirmation state (chip in header + gating
  // for View/Send Quote Package buttons). Refetched on component
  // mount + after Confirm/Revoke in BuildConfirmationStrip.
  const [buildConfirmed, setBuildConfirmed] = useState(null);
  const [buildConfirmationRefreshKey, setBuildConfirmationRefreshKey] = useState(0);
  useEffect(() => {
    const h = () => setBuildConfirmationRefreshKey(k => k + 1);
    window.addEventListener('rtr:build-confirmation-changed', h);
    return () => window.removeEventListener('rtr:build-confirmation-changed', h);
  }, []);
  const [bookingForm, setBookingForm] = useState({ date: '', time: '', appraiser_id: '', appraiser_name: '' });

  useEffect(() => {
    api.get('/contractors').then(r => {
      setContractors(r.data || []);
      // Find the owner contractor as default
      const owner = (r.data || []).find(c => c.is_owner);
      if (owner) setBookingForm(prev => ({ ...prev, appraiser_id: owner._id, appraiser_name: owner.contractor_name }));
    }).catch(() => {});
  }, [api]);

  // Go-Live Batch S · fetch build confirmation state for header chip.
  // Refetches on id change AND on buildConfirmationRefreshKey bump (fired
  // after ANY child updates the state via the BuildConfirmationStrip).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get(`/appraisals/${id}/build/confirmation`)
      .then(r => { if (!cancelled) setBuildConfirmed(r.data?.build_confirmation || null); })
      .catch(() => { if (!cancelled) setBuildConfirmed(null); });
    return () => { cancelled = true; };
  }, [api, id, buildConfirmationRefreshKey]);

  // Open the merged Quote Package PDF (Cover + Company Overview + Quotation).
  // Uses the same download-token pattern the rest of the report links use.
  const openQuotePackage = async () => {
    try {
      const { data } = await api.post('/reports/download-token');
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${base}/api/reports/quote-package/${id}?disposition=inline&token=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      // Fallback — direct link (will require auth cookie, works in-app)
      const base = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${base}/api/reports/quote-package/${id}?disposition=inline`, '_blank', 'noopener');
    }
  };


  // One rulebook: the server asks for a reason (409 needs_confirm) when a pick skips unfinished runner stages.
  const updateStatus = async (status, reason) => {
    if (status === 'Appraisal Booked' && !reason) {
      setShowBookingModal(true);
      return;
    }
    try {
      setStatusBusy(true);
      await api.patch(`/appraisals/${id}/status`, { appraisal_status: status, ...(reason ? { reason } : {}) });
      setPendingStatus(null);
      load();
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.code === 'needs_confirm') {
        setPendingStatus({ value: status, toLabel: stageLabel(stages, status), fromLabel: stageLabel(stages, data?.appraisal_status), unfinished: d.unfinished || [] });
      } else {
        alert(d?.detail || e.message || 'Status change failed');
      }
    } finally { setStatusBusy(false); }
  };

  // Promote an Instant Free appraisal in-place into a Detailed 1-Comment
  // (single-defect) inspection. Keeps the appraisal_no / A#### ID unchanged
  // so the customer-facing reference is preserved. Just bumps quote_type_ref
  // and resets the workflow status.
  // Batch T Phase 4 — canonical refs post-consolidation (was: Free Estimate
  // → Inspection Appraisal, now Instant Free → Detailed 1-Comment).
  const promoteToInspection = async () => {
    if (!window.confirm('Promote this Instant Free appraisal into a full Detailed 1-Comment inspection? The Appraisal ID stays the same, but the workflow becomes the detailed inspection report.')) return;
    try {
      await api.put(`/appraisals/${id}`, {
        quote_type_ref: 'Detailed 1-Comment',
        appraisal_status: data.appraisal_status === 'Completed' ? 'Completed' : 'Client Data',
      });
      load();
    } catch (e) { alert('Failed to promote: ' + (e?.response?.data?.detail || e.message)); }
  };

  const confirmBooking = async () => {
    if (!bookingForm.date) { alert('Please select a date for the appraisal'); return; }
    if (!bookingForm.appraiser_id) { alert('Please select an appraiser'); return; }
    await api.patch(`/appraisals/${id}/status`, {
      appraisal_status: 'Appraisal Booked',
      appraisal_booked_date: bookingForm.date,
      appraisal_time: bookingForm.time,
      appraiser_id: bookingForm.appraiser_id,
      appraiser_name: bookingForm.appraiser_name,
    });
    setShowBookingModal(false);
    load();
  };

  const saveAppraisal = async () => {
    // If a new appraiser name was typed, persist it back to the dropdown list
    const ap = (form.appraisal_by || '').trim();
    const known = dropdowns.appraisers || [];
    if (ap && !known.includes(ap)) {
      try { await api.post('/dropdowns/appraisers/add', { value: ap }); } catch {}
    }
    // Wave 2 · H9 fix — build a *diff-only* payload so untouched fields
    // (e.g. a stale `roof_subtype`, `service_option`, or `quote_type_ref`
    // sitting in `form` state) can never silently overwrite the DB. The
    // backend PUT already uses `exclude_unset=True` (Iter163), so
    // omitting a field means "leave as-is".
    //
    // We compare each editable field in `form` against the pristine
    // snapshot captured in `originalRef` at load time. Only fields that
    // genuinely changed are included in the PUT body.
    const editableKeys = [
      'client_id', 'property_id', 'appraisal_date', 'appraisal_by',
      'roof_condition', 'roof_requirements', 'ridge_condition',
      'tile_condition', 'valley_condition', 'barge_condition',
      'sheet_condition', 'spouting_condition', 'fastening_condition',
      'flashing_condition', 'appraisal_condition', 'quote_type_ref',
      'inspection_comments', 'service_option', 'roof_subtype',
      'appraisal_status', 'appraisal_action_general',
      'appraisal_comments', 'contract_amount',
      'amount_due', 'job_days_expected', 'contractor_name',
      'new_colour_choice', 'insurance_co_name', 'assessor_name',
      'claim_number',
    ];
    const original = originalRef.current || {};
    const isEqual = (a, b) => {
      // Cheap deep-equal for scalars, arrays, and plain objects.
      if (a === b) return true;
      if (a == null && b == null) return true;
      if (a == null || b == null) return false;
      if (typeof a !== typeof b) return false;
      if (typeof a !== 'object') return false;
      try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
    };
    const payload = {};
    for (const k of editableKeys) {
      if (!isEqual(form[k], original[k])) {
        payload[k] = form[k];
      }
    }
    // Quotation Type goes through its own guarded endpoint (single source of truth).
    if ('quote_type_ref' in payload) {
      try {
        await api.patch(`/appraisals/${id}/quote-type`, { quote_type_ref: payload.quote_type_ref || null });
        window.dispatchEvent(new Event('rtr:quote-type-changed'));
        delete payload.service_option;
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not change Quotation Type');
        return;
      }
      delete payload.quote_type_ref;
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      load();
      return;
    }
    try {
      await api.put(`/appraisals/${id}`, payload);
    } catch (err) {
      // Wave 2 · H9 backend canary — 409 means the server refused to
      // change one of the guarded pipeline fields (roof_subtype /
      // service_option / quote_type_ref). Surface the reason to the
      // operator instead of silently swallowing it.
      if (err?.response?.status === 409) {
        toast.error(err.response.data?.detail || 'Save blocked by canary — pipeline field change refused.');
        return;
      }
      toast.error(err?.response?.data?.detail || 'Save failed');
      return;
    }
    setEditing(false);
    load();
  };

  const addQuoteItem = async () => {
    if (!newItem) return;
    await api.post('/quote-items', { ...newItem, appraisal_id: id });
    setNewItem(null);
    load();
  };

  const deleteQuoteItem = async (itemId) => {
    await api.delete(`/quote-items/${itemId}`);
    load();
  };

  const updateQuoteItem = async () => {
    if (!editingItem?._id) return;
    await api.put(`/quote-items/${editingItem._id}`, editingItem);
    setEditingItem(null);
    load();
  };

  const [pdfLoading, setPdfLoading] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [emailAllLoading, setEmailAllLoading] = useState(false);
  // Sprint 2 Order 4 — purchase-orders UI retired.

  const emailAllContractors = async () => {
    if (emailAllLoading) return;
    if (!window.confirm(
      "Send a personalised Contractor Sheet PDF to every contractor tagged on this appraisal?\n\n" +
      "If SMTP isn't configured yet, each send will be queued in the email log for retry once you wire SMTP."
    )) return;
    setEmailAllLoading(true);
    try {
      const { data } = await api.post(`/reports/contractor-sheet/${id}/email-all`, {});
      const lines = [];
      if (data.sent)    lines.push(`✓ Sent: ${data.sent}`);
      if (data.logged)  lines.push(`◷ Logged (no SMTP): ${data.logged}`);
      if (data.skipped) lines.push(`⚠ Skipped (no email): ${data.skipped}`);
      if (data.failed)  lines.push(`✗ Failed: ${data.failed}`);
      const heading = data.smtp_configured ? "Bulk send complete" : "SMTP not configured — queued in email log";
      toast.success(`${heading}\n${lines.join("\n")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk send failed");
    } finally {
      setEmailAllLoading(false);
    }
  };

  // Friendly fallback for the Contractor Sheet PDF — when the appraisal
  // has no `contractor_id` set as the primary contractor, we prompt the
  // operator to pick one instead of letting the backend return the
  // unfriendly "No primary contractor on this appraisal" 400 error.
  const [contractorPromptOpen, setContractorPromptOpen] = useState(false);
  const [contractorPromptId, setContractorPromptId] = useState('');
  const openContractorSheet = () => {
    if (data?.contractor_id) {
      openPdfViewer('contractor-sheet');
    } else {
      setContractorPromptId('');
      setContractorPromptOpen(true);
    }
  };
  const confirmContractorSheet = async (alsoSaveAsPrimary) => {
    if (!contractorPromptId) return;
    if (alsoSaveAsPrimary && data) {
      // Persist the pick on the appraisal so the next click is one-tap.
      try {
        await api.put(`/appraisals/${id}`, { contractor_id: contractorPromptId });
        setData(d => ({ ...d, contractor_id: contractorPromptId }));
      } catch { /* non-fatal — still open the PDF */ }
    }
    setContractorPromptOpen(false);
    openPdfViewer('contractor-sheet', { contractor_id: contractorPromptId });
  };

  const openPdfViewer = (type, extraParams = null) => {
    const search = extraParams ? `?${new URLSearchParams(extraParams).toString()}` : '';
    navigate(`/pdf-viewer/${type}/${id}${search}`);
    // After triggering the PDF viewer for a customer-facing report, ask the
    // operator if they'd like to bump the appraisal status one step. Only
    // certain types map to a stage transition; everything else is a no-op.
    const eventMap = {
      'quotation':            'quotation_pdf_generated',
      'quotation-detailed':   'detailed_quotation_pdf_generated',
      'certificate-of-completion': 'certificate_pdf_generated',
    };
    const evt = eventMap[type];
    if (evt && data?.appraisal_status) {
      // small delay so the toast appears once the viewer tab/window is up
      setTimeout(() => {
        promptStageBump({
          api,
          appraisalId: id,
          currentStatus: data.appraisal_status,
          eventKey: evt,
          onAdvanced: () => loadData(),
        });
      }, 800);
    }
  };

  const generateInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const { data: inv } = await api.post(`/invoices/generate/${id}`);
      // Prompt the bump BEFORE navigating away so the operator sees the toast.
      if (data?.appraisal_status) {
        promptStageBump({
          api,
          appraisalId: id,
          currentStatus: data.appraisal_status,
          eventKey: 'invoice_created',
          onAdvanced: () => loadData(),
        });
      }
      navigate(`/invoices/${inv._id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to generate invoice');
    }
    setInvoiceLoading(false);
  };

  // Wave 2.2 (2026-02) — `cloneToQuote` handler DELETED.
  // Rationale: the button POSTed to `/appraisals/{id}/clone` which
  // (a) copied `appraisal_components` (legacy dead-ish collection)
  //     but not `component_lines` → cloned quote opened empty → bug H5,
  // (b) set `appraisal_status='Quoted'` which is already available
  //     as a normal status-pill transition on the same record,
  // (c) wrote `source_appraisal_id` which is never READ anywhere in
  //     the codebase.
  // Fix by deletion: operator uses the existing "Quoted" status pill
  // to move the appraisal into quote state. No data lost, no workflow lost.

  // Roof detail CRUD
  // Iter168 — fire a global event after every CRUD so per-builder
  // BuilderRoofDetailsPanel instances live-refresh without needing
  // the operator to navigate away and back.
  // Iter171 — surface a clear toast instead of silently no-op'ing when
  // the appraisal isn't linked to a property; previously operators
  // hit Save, the form closed, and nothing persisted (huge confusion).
  const addRoofDetail = async () => {
    if (!newRoof) return;
    if (!data?.property_id) {
      toast.error('Link a property to this appraisal first — Roof Details need a property to save against.');
      return;
    }
    try {
      await api.post('/roof-details', { ...newRoof, property_id: data.property_id });
      setNewRoof(null);
      load();
      fireRoofDetailsChanged();
      toast.success('Roof detail saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save roof detail');
    }
  };

  const updateRoofDetail = async () => {
    if (!editRoof?._id) return;
    if (!data?.property_id) {
      toast.error('Link a property to this appraisal first.');
      return;
    }
    const { _id, ...rest } = editRoof;
    try {
      await api.put(`/roof-details/${_id}`, { ...rest, property_id: data.property_id });
      setEditRoof(null);
      load();
      fireRoofDetailsChanged();
      toast.success('Roof detail updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update roof detail');
    }
  };

  const deleteRoofDetail = async (roofId) => {
    if (!window.confirm('Delete this roof detail?')) return;
    try {
      await api.delete(`/roof-details/${roofId}`);
      load();
      fireRoofDetailsChanged();
      toast.success('Roof detail deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete roof detail');
    }
  };

  // Adjacent appraisal navigation
  const [adjacentIds, setAdjacentIds] = useState({ prev: null, next: null });
  useEffect(() => {
    api.get(`/appraisals?page=1&per_page=5000&sort_by=created_at&sort_dir=desc`).then(({ data: d }) => {
      const ids = (d.items || []).map(a => a._id);
      const idx = ids.indexOf(id);
      setAdjacentIds({ prev: idx > 0 ? ids[idx - 1] : null, next: idx < ids.length - 1 ? ids[idx + 1] : null });
    }).catch(() => {});
  }, [api, id]);

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;
  if (!data) return null;

  const client = data.client || {};
  const property = data.property || {};

  // iter303ak-fix4 · Embed mode early-render — strip out sidebar / nav /
  // timeline / booking / all tabs except Roof Appraisal. Just the tab
  // content + a slim top strip with a "Save & Return to Runner" button.
  if (isEmbedRoofAppraisal) {
    return (
      <div data-testid="appraisal-embed-roof-appraisal" className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Slim top strip with identity + return-to-runner CTA */}
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-600 font-semibold">
                Workflow Runner · Roof Appraisal Popout
              </div>
              <div className="text-sm text-indigo-900 mt-1">
                Fill in this Roof Appraisal tab, then click <b>Save &amp; Return to Runner</b> below. The runner will auto-refresh when this window closes.
              </div>
              <div className="flex items-center gap-2 text-xs text-indigo-700 mt-2">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded">P{data.property.property_no}</span> : null}
                {data?.client ? <span>{[data.client.name_first, data.client.name_last].filter(Boolean).join(' ')}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="embed-close-btn"
              onClick={() => window.close()}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded"
              title="Close without saving — runner will detect and refresh"
            >
              Close window
            </button>
          </div>

          {/* Just the Roof Appraisal tab content. All other tabs hidden.
              embedReturnToRunner={true} tells the tab to render its Save
              button as "Save & Return to Runner" and window.close() on
              success. */}
          <RoofAppraisalTab
            api={api}
            appraisalId={id}
            appraisal={data}
            roofs={roofDetails}
            onSaved={loadData}
            onJumpTab={() => {}}
            embedReturnToRunner
          />
        </div>
      </div>
    );
  }

  // iter303ak-fix14 · Quote Builder embed. Renders only the Builder tab
  // + subtab strip in a slim popout wrapper. Sub-tab pre-selected via
  // ?builder=<key>. Runner poller closes on window.close().
  if (isEmbedQuoteBuilder) {
    const requestedSubtab = searchParams.get('builder') || '';
    const activeSubtab = childBuilderOf(data) || (BUILDER_SUBTABS.some(b => b.key === requestedSubtab)
      ? requestedSubtab
      : (builderSubtab || QUOTE_TYPE_REF_TO_SUBTAB(form.quote_type_ref) || SERVICE_TO_SUBTAB[form.service_option] || 'reroof'));
    const effectiveAppraisal = roofPreview ? { ...(data || {}), ...roofPreview } : data;
    return (
      <div data-testid="appraisal-embed-quote-builder" className="min-h-screen bg-slate-50 p-6">
        {/* Runner builders auto-push on close — hide the manual Push buttons. */}
        <style>{'[data-testid="reroof-push-to-components-btn"],[data-testid="painting-push-btn"],[data-testid="guttering-push"],[data-testid="repairs-push-btn"]{display:none!important}' + (data?.parent_appraisal_id && !childBuilderOf(data) ? '[data-testid="painting-quote-letter-block"]{display:none!important}' : '') + ''}</style>
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-600 font-semibold">
                Workflow Runner · Quote Builder Popout
              </div>
              <div className="text-sm text-indigo-900 mt-1">
                Build this quote — the Runner will pick up where you left off when this window closes.
              </div>
              <div className="flex items-center gap-2 text-xs text-indigo-700 mt-2">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded">P{data.property.property_no}</span> : null}
                {data?.client ? <span>{[data.client.name_first, data.client.name_last].filter(Boolean).join(' ')}</span> : null}
                <span className="px-2 py-0.5 bg-white border border-indigo-200 rounded uppercase font-semibold">{BUILDER_SUBTABS.find(b => b.key === activeSubtab)?.label || activeSubtab}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* iter303ak-fix16 · Master Diagram nested popout. */}
              <button
                type="button"
                data-testid="embed-open-annotator-btn"
                onClick={openAnnotatorPopout}
                className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 inline-flex items-center gap-1.5"
                title="Open the annotated Master Roof Plan in a nested popup — Save closes it and refreshes this builder"
              >
                <MapPinned size={12} strokeWidth={2.5} /> Master Diagram
              </button>
              {/* iter303ak-fix18 · Manual "Pull from Diagram" — safety net
                  alongside the auto-push (on annotator close + on builder
                  mount). Shows a full component-type diff toast below. */}
              <button
                type="button"
                data-testid="embed-pull-diagram-btn"
                onClick={() => pullFromDiagram({ silent: false })}
                disabled={pulling}
                className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                title="Re-run the diagram → component-lines sweep with the latest annotations"
              >
                <RefreshCw size={12} strokeWidth={2.5} className={pulling ? 'animate-spin' : ''} /> {pulling ? 'Pulling…' : 'Pull from Diagram'}
              </button>
              <button
                type="button"
                data-testid="embed-close-btn"
                onClick={() => window.close()}
                className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded"
                title="Close and return to the runner"
              >
                Close window
              </button>
            </div>
          </div>

          {data?.parent_appraisal_id && !childBuilderOf(data) && (
            <>
              <QuotationTypeBar appraisal={{ ...data, id }} roofType={roofDetails[0]?.roof_type || ''} onChanged={loadData} />
              <div className="flex justify-end -mt-2">
                <button data-testid="embed-add-oneoff-component-btn"
                  onClick={() => window.open(`/appraisals/${id}?embed=components`, `rtr-components-${id}`, 'width=1400,height=900')}
                  className="text-xs font-semibold text-[#0F4F1B] hover:underline">+ Add a one-off component to this quote</button>
              </div>
            </>
          )}

          {/* iter303ak-fix18 · Empty-state banner — proactive prompt when
              this appraisal has no component_lines yet. Two actions:
              open the Master Diagram (to add pins) or Pull now (in case
              annotations exist but never got pushed). */}
          {componentsEmpty && (
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3 shadow-sm" data-testid="embed-components-empty-banner">
              <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
              <div className="flex-1 text-sm text-amber-900">
                <div className="font-semibold">This builder has no components yet.</div>
                <div className="text-xs mt-0.5">Click <b>Master Diagram</b> to add pins, or <b>Pull from Diagram</b> to sweep annotations that were saved earlier.</div>
              </div>
              <button
                onClick={() => pullFromDiagram({ silent: false })}
                disabled={pulling}
                className="px-3 py-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="embed-empty-pull-btn"
              >
                <RefreshCw size={12} strokeWidth={2.5} className={pulling ? 'animate-spin' : ''} /> Pull now
              </button>
            </div>
          )}

          {/* iter303ak-fix18 · Pull diff toast — shows after every manual
              pull with a full per-component-type summary. */}
          {pullDiffToast && (
            <div
              className={`rounded-md border-2 px-4 py-2.5 flex items-center gap-3 shadow-sm ${
                pullDiffToast.isError
                  ? 'border-rose-300 bg-rose-50'
                  : pullDiffToast.isNoop
                  ? 'border-slate-300 bg-slate-50'
                  : 'border-emerald-300 bg-emerald-50'
              }`}
              data-testid="embed-pull-diff-toast"
            >
              {pullDiffToast.isError
                ? <AlertCircle className="w-4 h-4 text-rose-700 shrink-0" />
                : <RefreshCw className="w-4 h-4 text-emerald-700 shrink-0" />}
              <div className={`flex-1 text-sm ${pullDiffToast.isError ? 'text-rose-900' : 'text-emerald-900'}`}>
                <div className="font-semibold">{pullDiffToast.isError ? 'Pull failed' : (pullDiffToast.isNoop ? 'No changes' : 'Diagram pulled')}</div>
                <div className="text-xs mt-0.5">{pullDiffToast.summary}</div>
              </div>
              <button
                onClick={() => setPullDiffToast(null)}
                className="text-xs text-slate-500 hover:text-slate-800 px-2"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          {/* iter303ak-fix16 · Diff toast — visible after Master Diagram
              popout closes. Shows pins-added / pins-moved counts vs the
              pre-open snapshot with an Undo button. */}
          {annotatorDiffToast && (
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3 shadow-sm" data-testid="embed-annotator-diff-toast">
              <MapPinned className="w-4 h-4 text-amber-700 shrink-0" />
              <div className="flex-1 text-sm text-amber-900">
                <div className="font-semibold">Master Diagram updated</div>
                <div className="text-xs mt-0.5">{annotatorDiffToast.summary}</div>
              </div>
              <button
                onClick={undoAnnotatorChanges}
                className="px-3 py-1.5 text-xs font-semibold bg-white border border-amber-400 hover:bg-amber-100 text-amber-800 rounded"
                data-testid="embed-annotator-undo-btn"
              >
                Undo
              </button>
              <button
                onClick={() => setAnnotatorDiffToast(null)}
                className="text-xs text-amber-700 hover:text-amber-900 px-2"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          <div className="space-y-3" data-testid="builder-tab-embed">
            <div className="flex items-center gap-3 flex-wrap border-b border-slate-200 pb-1 bg-white rounded-t-md px-2">
              <div className="flex items-center gap-1">
                {subtabsFor(data).map((b) => (
                  <button
                    key={b.key}
                    data-testid={`builder-subtab-${b.key}`}
                    onClick={() => setBuilderSubtab(b.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                      activeSubtab === b.key
                        ? 'border-cyan-600 text-cyan-700 font-semibold'
                        : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            {activeSubtab === 'reroof' && (
              <DraftBuilderGate api={api} appraisal={data} builder="reroof" onChanged={loadData}>
                {(bid) => <ReroofBuilderTab api={api} appraisalId={bid} refreshKey={builderRefreshKey} />}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'remedial_repairs' && (
              <RemedialRepairsBuilderTab api={api} appraisalId={id} appraisal={effectiveAppraisal} onRefresh={loadData} onJumpToPin={() => {}} />
            )}
            {activeSubtab === 'repairs' && (
              <DraftBuilderGate api={api} appraisal={data} builder="repairs" onChanged={loadData}>
                {(bid, doc, reload) => (
                  <RepairsBuilderTab api={api} appraisalId={bid} appraisal={reload ? { ...(doc || {}), ...(roofPreview || {}) } : effectiveAppraisal} onRefresh={reload || loadData} onOpenAnnotator={() => {}} />
                )}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'painting' && (
              <DraftBuilderGate api={api} appraisal={data} builder="painting" onChanged={loadData}>
                {(bid, doc, reload) => (
                  <PaintingBuilderTab api={api} appraisalId={bid} appraisal={reload ? { ...(doc || {}), ...(roofPreview || {}) } : effectiveAppraisal} onRefresh={reload || loadData} />
                )}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'restoration' && (
              <RestorationBuilderTab api={api} appraisalId={id} appraisal={effectiveAppraisal} onRefresh={loadData} onOpenAnnotator={() => {}} />
            )}
            {activeSubtab === 'maintenance' && (
              <DraftBuilderGate api={api} appraisal={data} builder="maintenance" onChanged={loadData}>
                {(bid) => <MaintenanceBuilderTab api={api} appraisalId={bid} />}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'guttering' && (
              <DraftBuilderGate api={api} appraisal={data} builder="guttering" onChanged={loadData}>
                {(bid) => <GutteringBuilderTab api={api} appraisalId={bid} />}
              </DraftBuilderGate>
            )}
          </div>
        </div>
      </div>
    );
  }
  // iter303ak-fix24 · T30 Components embed. Popout of the Components
  // tab for a child appraisal — the runner opens this to let the
  // operator review/correct the auto-pushed component_lines. Save &
  // Return closes the window and the runner poller advances.
  if (isEmbedPricing || isEmbedWording) {
    return (
      <div data-testid={isEmbedPricing ? 'appraisal-embed-pricing' : 'appraisal-embed-wording'} className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-3xl mx-auto space-y-4">
          <EmbedStrip data={data} testId="embed-strip"
            title={isEmbedPricing ? 'Pricing & discounts' : 'Wording'}
            hint={isEmbedPricing ? 'Adjust the markup and discounts for this quote, then Save & Return.' : 'Edit the wording that appears on the documents, then Save & Return.'}
            onBeforeClose={isEmbedWording ? () => wordingSaveRef.current?.() : undefined} />
          {isEmbedPricing
            ? <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center justify-between"><span className="text-sm text-slate-600">The pricing panel opens on the right. Re-open it here if you close it.</span><PricingDrawer api={api} appraisalId={id} defaultOpen footer={
                <button data-testid="pricing-drawer-save-return-btn" onClick={() => window.close()} className="mt-6 w-full py-2 rounded-md bg-[#0F4F1B] text-white text-sm font-semibold hover:bg-[#0a3c14]">Save &amp; Return (changes save as you go)</button>} /></div>
            : <WordingQuickForm api={api} appraisalId={id} data={data} registerSave={(fn) => { wordingSaveRef.current = fn; }} />}
        </div>
      </div>
    );
  }

  if (isEmbedComponents) {
    return (
      <div data-testid="appraisal-embed-components" className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
                Workflow Runner · Components Popout
              </div>
              <div className="text-sm text-emerald-900 mt-1">
                Review and edit the components for this quote. Close the window when done — the Runner will auto-refresh.
              </div>
              <div className="flex items-center gap-2 text-xs text-emerald-800 mt-2 flex-wrap">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-emerald-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-emerald-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-emerald-200 rounded">P{data.property.property_no}</span> : null}
                {(data?.quotation_type || data?.quote_type_ref) ? <span className="px-2 py-0.5 bg-white border border-emerald-200 rounded uppercase font-semibold">{data.quotation_type || data.quote_type_ref}</span> : null}
                {data?.variant_label ? <span>{data.variant_label}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="embed-close-btn"
              onClick={() => window.close()}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded shrink-0"
              title="Close and return to the runner"
            >
              Save &amp; Return
            </button>
          </div>
          <AppraisalComponentsTab api={api} appraisalId={id} isAdmin={isAdmin} refreshKey={componentsRefreshKey} onJumpTab={() => {}} />
        </div>
      </div>
    );
  }

  // iter303ak-fix24 · T30 Contractors embed. Popout of the Contractors
  // tab for a child appraisal — the runner opens this to let the
  // operator review contractor assignments + overrides.
  if (isEmbedContractors) {
    return (
      <div data-testid="appraisal-embed-contractors" className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
                Workflow Runner · Contractors Popout
              </div>
              <div className="text-sm text-purple-900 mt-1">
                Review the contractor assignments and rate overrides for this quote. Close the window when done — the Runner will auto-refresh.
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-800 mt-2 flex-wrap">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-purple-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-purple-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-purple-200 rounded">P{data.property.property_no}</span> : null}
                {(data?.quotation_type || data?.quote_type_ref) ? <span className="px-2 py-0.5 bg-white border border-purple-200 rounded uppercase font-semibold">{data.quotation_type || data.quote_type_ref}</span> : null}
                {data?.variant_label ? <span>{data.variant_label}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="embed-close-btn"
              onClick={() => window.close()}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded shrink-0"
              title="Close and return to the runner"
            >
              Save &amp; Return
            </button>
          </div>
          <ContractorOverviewTab api={api} appraisalId={id} />
        </div>
      </div>
    );
  }

  // iter303ak-fix27 · T50 Materials embed. Popout of the Materials tab
  // for a child appraisal — runner opens this to let the operator
  // review the per-supplier materials breakdown and download orders.
  if (isEmbedMaterials) {
    return (
      <div data-testid="appraisal-embed-materials" className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">
                Workflow Runner · Materials Popout
              </div>
              <div className="text-sm text-teal-900 mt-1">
                Review the per-supplier materials breakdown for this quote. Close when done — the Runner will auto-refresh.
              </div>
              <div className="flex items-center gap-2 text-xs text-teal-800 mt-2 flex-wrap">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-teal-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-teal-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-teal-200 rounded">P{data.property.property_no}</span> : null}
                {data?.quote_type_ref ? <span className="px-2 py-0.5 bg-white border border-teal-200 rounded uppercase font-semibold">{data.quote_type_ref}</span> : null}
                {data?.variant_label ? <span>{data.variant_label}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="embed-close-btn"
              onClick={() => window.close()}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded shrink-0"
              title="Close and return to the runner"
            >
              Save &amp; Return
            </button>
          </div>
          <MaterialsTab api={api} appraisalId={id} />
        </div>
      </div>
    );
  }

  // iter303ak-fix29 · Job Execution embed (Work In Progress T50).
  if (isEmbedJobExecution) {
    return (
      <div data-testid="appraisal-embed-job-execution" className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 flex items-start justify-between gap-4 sticky top-0 z-10 shadow-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-violet-700 font-semibold">
                Workflow Runner · Job Execution Popout
              </div>
              <div className="text-sm text-violet-900 mt-1">
                Capture the per-child work checklist + extras + contractor sign-off. Sign off &amp; close returns you to the Runner.
              </div>
              <div className="flex items-center gap-2 text-xs text-violet-800 mt-2 flex-wrap">
                {data?.appraisal_no ? <span className="px-2 py-0.5 bg-white border border-violet-200 rounded"><b>A{data.appraisal_no}</b></span> : null}
                {data?.client?.client_no ? <span className="px-2 py-0.5 bg-white border border-violet-200 rounded">C{data.client.client_no}</span> : null}
                {data?.property?.property_no ? <span className="px-2 py-0.5 bg-white border border-violet-200 rounded">P{data.property.property_no}</span> : null}
              </div>
            </div>
            <button
              type="button"
              data-testid="embed-close-btn"
              onClick={() => window.close()}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-white/60 rounded shrink-0"
              title="Close and return to the runner"
            >
              Close
            </button>
          </div>
          <JobExecutionPanel appraisalId={id} />
        </div>
      </div>
    );
  }





  // iter303ak-fix16 · Annotator embed. Renders SlotAnnotatorModal
  // in a full-window popout. Save & close closes the window; parent
  // Builder popout listens via `window.closed` and auto-refreshes.
  if (isEmbedAnnotator) {
    return (
      <div data-testid="appraisal-embed-annotator" className="min-h-screen bg-slate-900">
        <div className="p-3 border-b border-slate-800 bg-slate-950 flex items-center justify-between text-xs text-slate-300">
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-widest font-semibold text-orange-400">Annotator Popout</span>
            {data?.appraisal_no ? <span className="px-2 py-0.5 bg-slate-800 rounded"><b>A{data.appraisal_no}</b></span> : null}
            <span className="opacity-70">Roof Plan · edit pins, then Save &amp; return</span>
          </div>
          <button
            type="button"
            data-testid="embed-close-annotator"
            onClick={() => window.close()}
            className="px-3 py-1 rounded text-slate-400 hover:text-white hover:bg-slate-800"
            title="Close and return to the Builder"
          >
            Close window
          </button>
        </div>
        <SlotAnnotatorModal
          open
          slotKey="roof_plan_template_id"
          slotLabel="Roof Plan · Master Diagram"
          slotColour="#D35400"
          appraisalId={id}
          templateId={data?.roof_plan_template_id || data?.roof_design_template_id || ''}
          roofPlanTemplateId={data?.roof_plan_template_id || data?.roof_design_template_id || ''}
          pins={data?.roof_diagram_annotations || []}
          roofProfile={(data?.roof_details || [])[0]?.roof_profile || ''}
          roofType={(data?.roof_details || [])[0]?.roof_type || ''}
          api={api}
          onPinsChange={(merged) => setData(d => ({ ...d, roof_diagram_annotations: merged }))}
          onClose={() => { try { window.close(); } catch { /* browser blocked */ } }}
        />
      </div>
    );
  }

  return (
    <div data-testid="appraisal-detail-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/appraisals')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#D35400] transition-colors font-medium">
          <ArrowLeft size={16} /> Back to Appraisals
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="prev-appraisal-btn" disabled={!adjacentIds.prev} onClick={() => navigate(`/appraisals/${adjacentIds.prev}`)} className="p-1.5 border border-slate-200 rounded-md text-slate-400 hover:text-slate-700 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronLeft size={16} /></button>
          <button data-testid="next-appraisal-btn" disabled={!adjacentIds.next} onClick={() => navigate(`/appraisals/${adjacentIds.next}`)} className="p-1.5 border border-slate-200 rounded-md text-slate-400 hover:text-slate-700 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><ChevronRight size={16} /></button>
          {/* Iter177 — Delete appraisal button. Opens the shared
              DeleteConfirmDialog with soft/hard option. Hard delete
              cascades to all dependent collections. */}
          <button
            data-testid="appraisal-delete-btn"
            onClick={() => setDeleteOpen(true)}
            className="p-1.5 border border-rose-200 rounded-md text-rose-500 hover:text-rose-700 hover:border-rose-400 hover:bg-rose-50 transition-colors"
            title="Delete this appraisal (soft / hard)"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      {data && !data.parent_appraisal_id && <JobQuotesPanel parentId={id} />}
      {data && !data.parent_appraisal_id && <OnlineAcceptanceCard api={api} jobId={id} />}
      <AppraisalTimeline appraisalId={id} refreshKey={pricingHistory.length + (data?.stage_history?.length || 0)} />

      {/* Iter171 — high-visibility banner when this appraisal has no
          linked property. Without one, every Roof Details / Builder /
          Components save silently no-ops. */}
      {!data.property_id && (
        <PropertyLinkBanner
          api={api}
          appraisalId={id}
          appraisal={data}
          onLinked={loadData}
        />
      )}

      {/* Iter172 — Client + Property summary strip, visible on every
          sub-tab. Single mount above the tab strip covers them all
          (Overview / Roof Appraisal / Diagrams / Builders / Components
          / Roof Details / Quote Calculation / Quote Items / Contractors
          / Quality) without per-tab boilerplate.
          Iter173 — also pass the appraisal's top-level client_id /
          property_id so the Edit links still appear when the embed is
          empty (orphaned client / brand-new link).
          Iter174 — pass appraisalId + appraisal + onLinked so the
          inline OrphanRelink popovers can PUT the corrected linkage. */}
      <AppraisalContextStrip
        api={api}
        appraisalId={id}
        appraisal={data}
        client={data.client || {}}
        property={data.property || {}}
        clientId={data.client_id || ''}
        propertyId={data.property_id || ''}
        onLinked={loadData}
      />
      {data.parent_appraisal_id && <ChildQuoteBanner api={api} appraisal={data} refreshKey={tab} onChanged={loadData} />}

      {/* Iter187 — sticky inspection booking summary at the top of the
          appraisal. Collapses to a one-line "Date · Time · Inspector"
          chip when not editing; expands to the full editor in place.
          The full editor also lives under the "Booking" tab. */}
      <BookingCard
        appraisalId={id}
        initialBooking={data.appraisal_booking}
        appraisers={dropdowns.appraisers || []}
        variant="sticky"
        onSaved={(updated) => setData(d => ({ ...d, ...updated }))}
      />

      <div className="relative bg-white border border-[#E2E2DF] rounded-md p-6 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: statusDotColour(data.appraisal_status) }} />
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D35400]/10">
              <ClipboardCheck size={22} className="text-[#D35400]" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5 inline-flex items-center gap-1.5">Quote <IdChip type="appraisal" value={data.appraisal_no || data.access_id || data._id?.slice(-6)} size="md" testId="appraisal-header-id-chip" /></p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight truncate">{client.name_first ? `${client.name_first} ${client.name_last || ''}`.trim() : (data.quote_type_ref || 'Appraisal')}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-sm text-slate-500">
                {/* Batch T Phase 4 · Canonical appraisal-kind badges. Legacy
                    values (Free Estimate / Inspection Appraisal / Appraisal
                    Quote) still render here so pre-consolidation appraisals
                    keep their coloured chip during UAT — post Access-reload
                    only the canonical refs will remain. */}
                {(data.quote_type_ref === 'Instant Free' || data.quote_type_ref === 'Free Estimate') && (
                  <span data-testid="kind-badge-free-estimate" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 bg-orange-50 text-[#A04000] border border-[#D35400]/30">Instant Free</span>
                )}
                {(data.quote_type_ref === 'Detailed 1-Comment' || data.quote_type_ref === 'Inspection Appraisal' || data.quote_type_ref === 'Appraisal Inspection') && (
                  <span data-testid="kind-badge-inspection" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 bg-emerald-50 text-[#234725] border border-[#316536]/30">Detailed 1-Comment</span>
                )}
                {(data.quote_type_ref === 'Detailed Multi' || data.quote_type_ref === 'Appraisal Report') && (
                  <span data-testid="kind-badge-multi" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 bg-teal-50 text-[#064E3B] border border-[#065F46]/30">Detailed Multi</span>
                )}
                {(data.quote_type_ref === 'Appraisal Quote') && (
                  <span data-testid="kind-badge-appraisal-quote" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 bg-blue-50 text-[#1E3A8A] border border-[#1E40AF]/30">Appraisal Quote</span>
                )}
                {data.quote_type_ref && !['Instant Free', 'Detailed 1-Comment', 'Detailed Multi', 'Free Estimate', 'Inspection Appraisal', 'Appraisal Inspection', 'Appraisal Report', 'Appraisal Quote'].includes(data.quote_type_ref) && <span className="flex items-center gap-1.5"><FileText size={14} className="text-slate-400" />{data.quote_type_ref}</span>}
                {/* Go-Live Batch S · Build Confirmed chip — small green
                    badge next to the quote_type chip. Only renders when
                    build_confirmation.confirmed=true on this appraisal. */}
                {buildConfirmed?.confirmed && (
                  <span
                    data-testid="header-build-confirmed-chip"
                    title={`Build confirmed by ${buildConfirmed.confirmed_by_name || 'operator'} · ${(buildConfirmed.confirmed_at || '').slice(0, 16).replace('T', ' ')} · ${buildConfirmed.line_count || 0} lines`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-300"
                  >
                    <CheckCircle2 size={11} strokeWidth={2.5} /> Build Confirmed
                  </span>
                )}
                {/* Iter303 — inline Quote-Type editor. Pencil next to
                    the current quote-type chip; click → dropdown of all
                    known quote_types + Save/Cancel. Saves via PATCH
                    /appraisals/:id/quote-type which back-syncs
                    service_option too. Also opens automatically when
                    the page is loaded with `?edit=quote_type`. */}
                {!editingQuoteType ? (
                  <button
                    data-testid="header-edit-quote-type-btn"
                    onClick={() => setEditingQuoteType(true)}
                    title="Change quotation type"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 border border-dashed border-slate-300 text-slate-500 hover:border-[#D35400] hover:text-[#D35400] transition-colors"
                  >
                    <Edit2 size={11} strokeWidth={2.4} /> Change
                  </button>
                ) : (
                  <span
                    data-testid="header-quote-type-editor"
                    className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-1 py-0.5 border border-[#D35400] bg-orange-50/60"
                  >
                    <QuotationTypeSelect api={api} roofType={(data?.roof_details || [])[0]?.roof_type || ''} current={data.quote_type_ref || ''}
                      disabled={quoteTypeSaving || !!buildConfirmed?.confirmed} testId="header-quote-type" onPick={(ref) => saveQuoteTypeInline(ref)} />
                    <button
                      data-testid="header-quote-type-cancel"
                      onClick={() => setEditingQuoteType(false)}
                      disabled={quoteTypeSaving}
                      className="p-0.5 rounded-full text-slate-500 hover:text-slate-900 hover:bg-white"
                      title="Cancel"
                    ><X size={12} strokeWidth={2.4} /></button>
                    {quoteTypeSaving && <span className="text-[9px] text-slate-500 pr-1">saving…</span>}
                  </span>
                )}
                {(() => {
                  // Roof material badge — surfaces tile vs metal at
                  // the top of the appraisal so every reviewer
                  // (estimator, office, contractor) sees at-a-glance
                  // which quoting playbook applies. Tile = lead
                  // aquaseals + hip flashings + pointing; Metal =
                  // EPDM Dektites + ridge caps. Tooltip shows the
                  // raw roof profile strings that drove the
                  // classification so the operator can debug.
                  const mat = classifyRoofMaterial(roofDetails);
                  if (!mat) return null;
                  const blob = roofDetails.map((r) => [r.description, r.roof_type, r.roof_profile].filter(Boolean).join(' · ')).filter(Boolean).join(' | ');
                  const cfg = {
                    tile:  { label: 'tile · concrete/clay',  cls: 'bg-zinc-100 text-zinc-800 border-zinc-300' },
                    metal: { label: 'metal · long-run',      cls: 'bg-sky-50 text-sky-800 border-sky-300' },
                    mixed: { label: 'mixed · tile + metal',  cls: 'bg-amber-50 text-amber-800 border-amber-300' },
                  }[mat];
                  return (
                    <span
                      data-testid={`roof-material-badge-${mat}`}
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded-full px-2 py-0.5 border ${cfg.cls}`}
                      title={`Roof material inferred from ${roofDetails.length} roof${roofDetails.length === 1 ? '' : 's'}: ${blob || '(no profile data)'}`}
                    >
                      <Home size={11} strokeWidth={2.4} /> {cfg.label}
                    </span>
                  );
                })()}
                {(client.name_first || client.name_last || client.company_name) && (
                  <span
                    data-testid="appraisal-header-client"
                    className="flex items-center gap-1.5 cursor-pointer hover:text-[#D35400] transition-colors"
                    onClick={() => data.client_id && navigate(`/clients/${data.client_id}`)}
                    title="Open client"
                  >
                    <User size={14} className="text-slate-400" />
                    <span>{client.is_company && client.company_name ? client.company_name : `${client.name_first || ''} ${client.name_last || ''}`.trim()}</span>
                    {(client.client_no || client.access_id) && (
                      <IdChip type="client" value={client.client_no || client.access_id} size="md" testId="appraisal-header-client-id" />
                    )}
                  </span>
                )}
                {property.property_address_street && (
                  <span
                    data-testid="appraisal-header-property"
                    className="flex items-center gap-1.5 cursor-pointer hover:text-[#D35400] transition-colors"
                    onClick={() => navigate(`/properties/${data.property_id}`)}
                    title="Open property"
                  >
                    <MapPin size={14} className="text-slate-400" />
                    <span>{property.property_address_num} {property.property_address_street}, {property.property_address_suburb}</span>
                    {(property.property_no || property.access_id) && (
                      <IdChip type="property" value={property.property_no || property.access_id} size="md" testId="appraisal-header-property-id" />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2 flex-shrink-0">
            {data.appraisal_date && (
              <span data-testid="appraisal-header-date" className="flex items-center gap-1.5 font-mono-num text-sm text-slate-600">
                <Calendar size={14} className="text-slate-400" />{new Date(data.appraisal_date).toLocaleDateString('en-NZ')}
              </span>
            )}
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${statusPillClasses(data.appraisal_status)}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusDotColour(data.appraisal_status) }} />
              {data.appraisal_status || 'New'}
            </span>
            {data.contract_amount > 0 && (
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">Contract</p>
                <p className="text-xl font-extrabold text-emerald-700 font-[Manrope] font-mono-num tracking-tight">${Number(data.contract_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            )}
          </div>
        </div>
        {/* Legacy archive accordion (Access-DB reload v5 · 2026-02-28)
            Shows only on imported appraisals so the panel stays invisible
            for the normal post-launch flow. */}
        {(data.import_source === 'access_migration' || data.legacy_access_id) && (
          <details className="mt-4 rounded-lg border border-amber-200 bg-amber-50/40" data-testid="legacy-archive">
            <summary className="cursor-pointer px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-amber-800 hover:bg-amber-100/60 rounded-lg">
              Legacy archive · imported from Access DB
            </summary>
            <div className="px-4 pb-3 pt-1 text-xs text-slate-700 space-y-2">
              {data.legacy_appraisal_status_raw && (
                <div><span className="font-semibold text-slate-500">Old status:</span> <code className="bg-white px-1 rounded border border-amber-200">{data.legacy_appraisal_status_raw}</code></div>
              )}
              {data.legacy_quote_type_label && (
                <div><span className="font-semibold text-slate-500">Old quote type:</span> {data.legacy_quote_type_label}</div>
              )}
              {data.legacy_appraisal_date && (
                <div><span className="font-semibold text-slate-500">Appraisal date:</span> {String(data.legacy_appraisal_date).slice(0, 10)}</div>
              )}
              {Number(data.legacy_contract_amount) > 0 && (
                <div><span className="font-semibold text-slate-500">Contract amount:</span> ${Number(data.legacy_contract_amount).toFixed(2)}</div>
              )}
              {data.legacy_work_completed_date && (
                <div><span className="font-semibold text-slate-500">Work completed:</span> {String(data.legacy_work_completed_date).slice(0, 10)}</div>
              )}
              {data.legacy_invoiced_date && (
                <div><span className="font-semibold text-slate-500">Invoiced:</span> {String(data.legacy_invoiced_date).slice(0, 10)}</div>
              )}
              {data.legacy_quote_description && (
                <div className="pt-1">
                  <div className="font-semibold text-slate-500 mb-1">Original quote description:</div>
                  <div className="whitespace-pre-wrap bg-white p-2 rounded border border-amber-200 text-[11px] leading-relaxed">{data.legacy_quote_description}</div>
                </div>
              )}
              {Array.isArray(data.legacy_component_lines) && data.legacy_component_lines.length > 0 && (
                <div className="pt-1">
                  <div className="font-semibold text-slate-500 mb-1">Original line items ({data.legacy_component_lines.length}):</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-[11px]">
                    {data.legacy_component_lines.map((ln, i) => (
                      <li key={i} data-testid={`legacy-line-${i}`}>
                        <span className="font-semibold">{ln.category}</span> · {ln.legacy_type_label}
                        {Number(ln.qty) > 0 && <> · qty {ln.qty}</>}
                        {Number(ln.length) > 0 && <> × {ln.length}m</>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="pt-1 text-[10px] text-slate-400 italic">Access ID: {data.legacy_access_id || data.access_id || '—'}</div>
            </div>
          </details>
        )}
        <div className="flex flex-wrap gap-1.5 mt-5 pt-4 border-t border-[#E2E2DF]">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 self-center mr-1">Status:</span>
          {stages.map(st => (
            <button key={st.key} data-testid={`status-btn-${st.key}`} disabled={st.value === data.appraisal_status || statusBusy} onClick={() => updateStatus(st.value)} className={`text-[11px] px-2.5 py-1 rounded-md border transition-all duration-200 ${st.value === data.appraisal_status ? 'bg-[#D35400] text-white border-[#D35400] shadow-sm font-semibold' : 'border-[#E2E2DF] text-slate-500 hover:border-[#D35400] hover:text-[#D35400] hover:bg-orange-50/50'}`}>
              {st.label}
            </button>
          ))}
          {data.appraisal_status && !stages.some(st => st.value === data.appraisal_status) && (
            <span data-testid="status-off-pipeline" className="text-[11px] px-2.5 py-1 rounded-md border border-dashed border-slate-300 text-slate-500 italic">
              {data.appraisal_status === 'legacy_pending_review' ? 'Legacy · Pending Review' : data.appraisal_status}
            </span>
          )}
          <StatusConfirmDialog pending={pendingStatus} busy={statusBusy} onCancel={() => setPendingStatus(null)}
            onConfirm={(reason) => updateStatus(pendingStatus.value, reason)} />
          <span className="mx-1 text-slate-300 self-center">|</span>
          {/* Wave 2.2 (2026-02) — "Convert to Quote" button DELETED. Use the "Quoted" status pill above instead. */}
          <button data-testid="send-update-btn" onClick={() => setShowSendScript(true)} className="text-[11px] px-3 py-1 rounded-md border border-[#D35400] text-[#D35400] hover:bg-[#D35400] hover:text-white font-semibold transition-all flex items-center gap-1">
            <Mail size={12} strokeWidth={2.5} /> Send Update
          </button>
          <button data-testid="appraisal-header-compare-btn" onClick={() => setShowCompare(true)} className="text-[11px] px-3 py-1 rounded-md border border-emerald-500 text-emerald-700 hover:bg-emerald-600 hover:text-white font-semibold transition-all flex items-center gap-1">
            <FileText size={12} strokeWidth={2.5} /> Compare Quotes
          </button>
          <CompareQuotesDialog api={api} appraisalId={id}
                                open={showCompare} onOpenChange={setShowCompare} />
          {data.quote_type_ref === 'Free Estimate' && (
            <button data-testid="promote-to-inspection-btn" onClick={promoteToInspection} className="text-[11px] px-3 py-1 rounded-md border border-[#316536] text-[#316536] hover:bg-[#316536] hover:text-white font-semibold transition-all flex items-center gap-1">
              Promote to Inspection Appraisal
            </button>
          )}
        </div>
        {/* Property base diagram — single source of truth. Changing this
            picker writes to BOTH the legacy `roof_design_template_id` AND
            all 5 named slots (Roof Plan / Roofing / Guttering / Concrete
            Restore / Painter) so the operator never ends up with mismatched canvases
            on the slot editors when pulling pins from Roof Plan. */}
        <div className="mt-3 pt-3 border-t border-[#E2E2DF] flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Main Roof Template:</span>
          <RoofDesignTemplatePicker
            value={data.roof_design_template_id || data.roof_plan_template_id || ''}
            onChange={async (newId) => {
              const tid = newId || '';
              await api.patch(`/appraisals/${id}/roof-template`, {
                roof_design_template_id:                  tid,
                roof_plan_template_id:                    tid,
                roofer_measurements_template_id:          tid,
                guttering_measurements_template_id:       tid,
                concrete_restore_measurements_template_id: tid,
                painter_measurements_template_id:          tid,
              });
              loadData();
            }}
          />
          {/* Iter209 — surface the link back to where the template is
              saved (the property record). Reassures the operator that
              the template is the property's saved Main Roof Template,
              not a per-appraisal one-off.
              Iter210 — when the appraisal's template DOESN'T match the
              property's saved one (operator overrode it mid-quote),
              offer a one-click "Push to Property" button so the
              override becomes the new property default. */}
          {(data.roof_design_template_id || data.roof_plan_template_id) && data.property?._id && (() => {
            const appraisalTpl = data.roof_design_template_id || data.roof_plan_template_id;
            const propertyTpl = data.property?.roof_template_id || '';
            const inSync = propertyTpl === appraisalTpl;
            if (inSync) {
              return (
                <span
                  data-testid="main-roof-template-source-hint"
                  className="text-[10px] text-slate-400 ml-1 inline-flex items-center gap-1"
                  title="This appraisal's diagram is sourced from the Property's saved Main Roof Template."
                >
                  ↗ Property's saved Main Roof Template
                </span>
              );
            }
            return (
              <span data-testid="main-roof-template-override-row" className="inline-flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-amber-700 ml-1 inline-flex items-center gap-1 font-semibold">
                  ⚠ Overridden — does not match Property's saved template
                </span>
                <button
                  type="button"
                  data-testid="main-roof-template-push-to-property"
                  onClick={async () => {
                    if (!window.confirm('Save this template as the Property\'s Main Roof Template?\n\nEvery future appraisal for this property will auto-pre-fill with this template instead of the previously-saved one.')) return;
                    try {
                      await api.patch(`/properties/${data.property._id}`, {
                        roof_template_id: appraisalTpl,
                      });
                      const { toast } = await import('sonner');
                      toast.success('Template pushed — now the Property\'s default Main Roof Template');
                      loadData();
                    } catch (e) {
                      const { toast } = await import('sonner');
                      toast.error(e?.response?.data?.detail || 'Failed to push template to property');
                    }
                  }}
                  title="Make this template the Property's saved default — every future appraisal will use it."
                  className="text-[10px] font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-400 text-emerald-700 bg-white hover:bg-emerald-50"
                >
                  ↑ Push to Property
                </button>
              </span>
            );
          })()}
          <span className="text-[10px] text-slate-400 ml-1">— drives the pin annotator below AND all 5 slots</span>
        </div>
        {/* Interactive diagram annotator — drop pins for Ridge/Hip/Valley/etc.
            measurements. The "Push to Components" button rolls the totals
            into the Components tab as a `roof_diagram` import source so
            they show up on the quote without overwriting manual lines. */}
        {/* Iter163 fix — show the annotator block when there's a
            template OR when annotations already exist (orphaned data
            from prior PUTs that wiped template_ids). The annotator
            itself renders its own "Pick a template first" empty state
            when templateId is missing; surfacing it lets the operator
            see/recover the orphaned annotation list. */}
        {((data.roof_design_template_id || data.roof_plan_template_id) || (data.roof_diagram_annotations && data.roof_diagram_annotations.length > 0)) && (
          <div className="mt-3 pt-3 border-t border-[#E2E2DF]">
            {!data.roof_design_template_id && !data.roof_plan_template_id && (data.roof_diagram_annotations?.length || 0) > 0 && (
              <div data-testid="orphan-annotations-warning" className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>⚠ {data.roof_diagram_annotations.length} pin{data.roof_diagram_annotations.length === 1 ? '' : 's'} saved without a template.</strong> Pick the Main Roof Template above so the annotated layout appears below — the pins will re-render automatically once a template is selected.
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {/* Opt-in: include diagram on the customer-facing Quotation PDF.
                  Default OFF — flip on when the customer asks for visual detail. */}
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors">
                <input
                  type="checkbox"
                  data-testid="toggle-include-diagram-on-quote"
                  checked={!!data.quotation_include_diagram}
                  onChange={async (e) => {
                    await api.patch(`/appraisals/${id}/quotation-include-diagram`, {
                      quotation_include_diagram: e.target.checked,
                    });
                    loadData();
                  }}
                  className="w-3.5 h-3.5 accent-[#D35400]"
                />
                <span className="font-semibold text-amber-800">Include annotated diagram on Quotation PDF</span>
                <span className="text-amber-600 text-[10px]">— flip on if the customer asks for detail</span>
              </label>
              {/* Opt-in: include rich side-by-side variant comparison
                  (Galv vs Zinc vs Maxx) on the customer-facing Quote
                  PDF. Default OFF; flip on when the client wants the
                  full kitchen-table comparison view on their quote. */}
              <label
                className={`flex items-center gap-2 text-xs cursor-pointer select-none px-2.5 py-1.5 rounded-md border transition-colors ${
                  data.quotation_include_variant_comparison
                    ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                }`}
                title="When ON, the Quotation PDF replaces the basic options strip with a rich side-by-side comparison table — chosen + every include-in-summary spec in columns with totals + $/m²"
              >
                <input
                  type="checkbox"
                  data-testid="toggle-include-variant-comparison-on-quote"
                  checked={!!data.quotation_include_variant_comparison}
                  onChange={async (e) => {
                    await api.patch(`/appraisals/${id}/quotation-include-variant-comparison`, {
                      quotation_include_variant_comparison: e.target.checked,
                    });
                    loadData();
                  }}
                  className="w-3.5 h-3.5 accent-emerald-600"
                />
                <span className={`font-semibold ${data.quotation_include_variant_comparison ? 'text-emerald-800' : 'text-slate-700'}`}>
                  Side-by-side variant comparison on Quotation PDF
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    data.quotation_include_variant_comparison
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-300 text-slate-700'
                  }`}
                  data-testid="variant-comparison-status-badge"
                >
                  {data.quotation_include_variant_comparison ? 'ON' : 'OFF'}
                </span>
              </label>
              {/* Email Quote to Client — grabs the current Quotation PDF
                  (variant-compare baked in when the toggle above is ON)
                  and ships it via the configured SMTP. When SMTP isn't
                  wired yet the email is logged to /email/log so nothing
                  is lost — operator sees a friendly notice. */}
              <button
                type="button"
                data-testid="email-quote-to-client-btn"
                onClick={() => setShowEmailQuoteModal(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold ring-1 ring-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-800 transition-colors"
                title="Email the Quotation PDF (variant-comparison baked in if the toggle is ON) to the client"
              >
                📧 Email quote to client
              </button>
              {/* Go-Live Batch S · Quote Package buttons — the full "cake"
                  (Cover Letter + Company Overview + Quotation Letter) as
                  one merged PDF. View opens preview in new tab; Send
                  opens EmailModal preconfigured with the package attached
                  and mark_quote_sent=true (auto-flips status on success). */}
              <button
                type="button"
                data-testid="view-quote-package-header-btn"
                onClick={openQuotePackage}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold ring-1 ring-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 transition-colors"
                title="Preview the full Quote Package PDF (Cover Letter + Company Overview + Quotation Letter, all merged)"
              >
                <ExternalLink size={12} strokeWidth={2.4} /> View Quote Package
              </button>
              <button
                type="button"
                data-testid="send-quote-package-header-btn"
                onClick={() => setShowSendPackageModal(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-emerald-700 hover:bg-emerald-800 text-white transition-colors"
                title="Email the Quote Package (cover + company overview + quotation) to the client — auto-flips status to Quote Sent on send"
              >
                📤 Send Quote Package
              </button>
            </div>
            {/* Cultural toolkit — surfaces greeting / thank-you / farewell
                in the client's language + upcoming culturally-significant
                dates. Renders an empty-state CTA when the client doesn't
                yet have a cultural_identity set. */}
            {data.client_id && (
              <div className="mt-3">
                <CulturalToolkitCard api={api} clientId={data.client_id} />
              </div>
            )}
            <RoofDiagramAnnotator
              appraisalId={id}
              templateId={data.roof_design_template_id || data.roof_plan_template_id}
              initialPins={data.roof_diagram_annotations}
              onChange={(pins) => setData(d => ({ ...d, roof_diagram_annotations: pins }))}
              slot="roof_plan"
              roofType={roofDetails[0]?.roof_type || ''}
              roofProfile={roofDetails[0]?.roof_profile || ''}
              onPushedToComponents={() => setComponentsRefreshKey(k => k + 1)}
              hidePush={!!childBuilderOf(data)}
            />
          </div>
        )}
        {/* Booking Modal */}
        {showBookingModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBookingModal(false)}>
            <div className="bg-white rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-200">
                <h2 className="text-base font-bold text-[#0F172A]">Book Appraisal</h2>
                <p className="text-xs text-slate-500 mt-1">Schedule a date and assign an appraiser</p>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Appraisal Date *</label>
                  <input data-testid="booking-date-input" type="date" required value={bookingForm.date} onChange={e => setBookingForm({ ...bookingForm, date: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                  <input data-testid="booking-time-input" type="time" value={bookingForm.time} onChange={e => setBookingForm({ ...bookingForm, time: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Appraiser *</label>
                  <select data-testid="booking-appraiser-select" value={bookingForm.appraiser_id} onChange={e => { const c = contractors.find(x => x._id === e.target.value); setBookingForm({ ...bookingForm, appraiser_id: e.target.value, appraiser_name: c ? c.contractor_name : '' }); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]">
                    <option value="">Select appraiser...</option>
                    {contractors.map(c => <option key={c._id} value={c._id}>{c.contractor_name}{c.is_owner ? ' (Owner)' : ''}</option>)}
                  </select>
                </div>
              </div>
              <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
                <button onClick={() => setShowBookingModal(false)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
                <button data-testid="confirm-booking-btn" onClick={confirmBooking} className="px-5 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] transition-colors">Book Appraisal</button>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#E2E2DF]">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 self-center mr-1">PDF Reports:</span>

          {/* ═══ SEND TO CUSTOMER ═══ */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="pdf-group-customer-trigger" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#D35400] text-white hover:bg-[#A04000] transition-all font-semibold">
                <Send size={13} />Send to Customer<ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" data-testid="pdf-group-customer-menu">
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Client-Facing PDFs</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="download-quotation-pdf" onClick={() => openPdfViewer('quotation')} className="cursor-pointer">
                <FileText size={13} className="text-[#D35400] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Quotation</span><span className="text-[10px] text-slate-500">Primary quote (Terms of Trade auto-appended)</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-quotation-detailed-pdf" onClick={() => openPdfViewer('quotation-detailed')} className="cursor-pointer">
                <FileText size={13} className="text-emerald-700 mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Quotation Letter</span><span className="text-[10px] text-slate-500">Personalised letter version</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-client-breakdown-pdf" onClick={() => openPdfViewer('client-breakdown')} className="cursor-pointer">
                <FileText size={13} className="text-amber-600 mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Client Breakdown</span><span className="text-[10px] text-slate-500">Per-component quantities + totals, hides rates</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-cover-letter-pdf" onClick={() => openPdfViewer('cover-letter')} className="cursor-pointer">
                <FileText size={13} className="text-[#1E40AF] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Cover Letter</span><span className="text-[10px] text-slate-500">5-paragraph personalised cover</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-appraisal-authorisation-pdf" onClick={() => openPdfViewer('appraisal-authorisation')} className="cursor-pointer">
                <FileText size={13} className="text-[#0EA5E9] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Auth Letter</span><span className="text-[10px] text-slate-500">Pre-inspection sign-and-return</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-terms-of-trade-pdf" onClick={() => openPdfViewer('terms-of-trade')} className="cursor-pointer">
                <FileText size={13} className="text-slate-700 mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Terms of Trade</span><span className="text-[10px] text-slate-500">Full / Simple toggle inside viewer</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-certificate-of-completion-pdf" onClick={() => openPdfViewer('certificate-of-completion')} className="cursor-pointer">
                <FileText size={13} className="text-[#0F4F1B] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Certificate of Completion</span><span className="text-[10px] text-slate-500">Post-payment warranty cert</span></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* ═══ INTERNAL / OFFICE ═══ */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="pdf-group-internal-trigger" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#6B46C1] text-white hover:bg-[#553C9A] transition-all font-semibold">
                <Building2 size={13} />Internal / Office<ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" data-testid="pdf-group-internal-menu">
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Office Only — Contains Costs</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="download-appraisal-pdf" onClick={() => openPdfViewer('appraisal')} className="cursor-pointer">
                <FileText size={13} className="text-[#6B46C1] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Appraisal</span><span className="text-[10px] text-slate-500">Internal appraisal summary</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-materials-pdf" onClick={() => openPdfViewer('materials')} className="cursor-pointer">
                <FileText size={13} className="text-[#4A6FA5] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Materials</span><span className="text-[10px] text-slate-500">Internal materials summary</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-profit-pdf" onClick={() => openPdfViewer('profit')} className="cursor-pointer">
                <FileText size={13} className="text-[#2F855A] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Profit Analysis</span><span className="text-[10px] text-slate-500">Internal profit breakdown</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-quote-calcs-pdf" onClick={() => openPdfViewer('quote-calcs')} className="cursor-pointer">
                <FileText size={13} className="text-[#D35400] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Quote Calcs</span><span className="text-[10px] text-slate-500">Internal / Customer audience toggle inside viewer</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-contractor-detailed-pdf" onClick={() => openPdfViewer('contractor-detailed')} className="cursor-pointer">
                <FileText size={13} className="text-[#9F1239] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Office Contractor Sheet</span><span className="text-[10px] text-slate-500">Multi-trade with rate + uplift columns</span></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* ═══ FIELD / ON-SITE ═══ */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="pdf-group-field-trigger" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#B45309] text-white hover:bg-[#92400E] transition-all font-semibold">
                <HardHat size={13} />Field / On-site<ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" data-testid="pdf-group-field-menu">
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Jobsite Operational</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="download-roof-diagram-appraisal-pdf" onClick={() => openPdfViewer('roof-diagram-appraisal')} className="cursor-pointer">
                <FileText size={13} className="text-[#7C2D12] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Roof Diagram Pack</span><span className="text-[10px] text-slate-500">Hand-sketch pack with KEY legend</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-job-tracking-sheet-pdf" onClick={() => openPdfViewer('job-tracking-sheet')} className="cursor-pointer">
                <FileText size={13} className="text-[#7E22CE] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Tracking Sheet</span><span className="text-[10px] text-slate-500">Live milestone tracker</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-work-sheet-pdf" onClick={() => openPdfViewer('work-sheet')} className="cursor-pointer">
                <FileText size={13} className="text-[#B45309] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Work Sheet</span><span className="text-[10px] text-slate-500">On-site history + checklist page 2</span></div>
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="download-materials-report-pdf" onClick={() => openPdfViewer('materials-report')} className="cursor-pointer">
                <FileText size={13} className="text-[#16A34A] mr-2" />
                <div className="flex flex-col"><span className="text-xs font-medium">Materials Report</span><span className="text-[10px] text-slate-500">Ordering + receipt checklist</span></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <OpenInDriveButton entity="job" id={id} />
          <a data-testid="appraisal-update-resend-btn" href={`/update-resend?job=${data?.parent_appraisal_id || id}`} className="inline-flex items-center text-xs px-3 py-1.5 rounded-md border border-[#D35400]/40 text-[#D35400] font-semibold hover:bg-orange-50">Update & Resend</a>
          {/* ═══ SAVE TO DISK (2026-02 Document Auto-Save MVP) ═══ */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="pdf-group-save-trigger" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] transition-all font-semibold">
                <Save size={13} />Save to Disk<ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80 p-2" data-testid="pdf-group-save-menu">
              <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Auto-named for your G Drive · Access-style</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="p-2 space-y-2">
                <SaveDocButton
                  appraisalId={id}
                  docKinds={['appraisal_confirmation', 'appraisal_authorisation']}
                  groupLabel="Appraisal Docs"
                  label="Appraisal Docs (Confirmation + Waiver)"
                  variant="outline"
                  className="w-full justify-start"
                  testId="save-appraisal-docs-pack"
                />
                <SaveDocButton
                  appraisalId={id}
                  docKinds={['quotation_detailed', 'cover_letter']}
                  groupLabel="Quotation Pack"
                  label="Quotation Pack (all children + Cover Letter)"
                  variant="outline"
                  className="w-full justify-start"
                  testId="save-quotation-pack"
                />
                <div className="pt-1 border-t border-slate-100" />
                <SaveDocButton
                  appraisalId={id}
                  docKind="quote_package"
                  label="Quote Package (merged)"
                  variant="outline"
                  className="w-full justify-start"
                  testId="save-quote-package-single"
                />
                <SaveDocButton
                  appraisalId={id}
                  docKind="quotation_detailed"
                  label="Quotation (this appraisal only)"
                  variant="outline"
                  className="w-full justify-start"
                  expandChildren={false}
                  testId="save-quotation-single"
                />
                <SaveDocButton
                  appraisalId={id}
                  docKind="terms_of_trade_simple"
                  label="Terms of Trade"
                  variant="outline"
                  className="w-full justify-start"
                  testId="save-terms-of-trade-single"
                />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* ═══ COPY EMAIL SCRIPT (2026-02 v1) ═══ */}
          <button
            type="button"
            onClick={() => setShowEmailScriptModal(true)}
            data-testid="email-script-trigger-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-all font-semibold"
          >
            <Mail size={13} />Email Script
          </button>

          <div className="h-5 w-px bg-slate-200 self-center" />

          {/* Contractor actions — standalone (not simple PDF launches) */}
          <button data-testid="download-contractor-sheet-pdf" onClick={openContractorSheet} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#0F4F1B] text-[#0F4F1B] hover:bg-[#0F4F1B] hover:text-white transition-all" title="Contractor Sheet — single contractor's labour contract, filtered to the appraisal's primary contractor."><FileText size={13} />Contractor Sheet</button>
          <button data-testid="email-all-contractors-btn" onClick={emailAllContractors} disabled={emailAllLoading} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14] transition-all disabled:opacity-50" title="One-click bulk send — generates a personalised Contractor Sheet PDF for every contractor tagged on this appraisal's quote items and emails each one their own copy in parallel."><Mail size={13} />{emailAllLoading ? 'Sending…' : 'Send to All Contractors'}</button>
          <ContractorSheetPicker appraisalId={id} onOpenPdf={openPdfViewer} />
          <button data-testid="open-foreman-mobile" onClick={() => window.open(`/m/job/${id}`, '_blank')} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-all" title="Open the Foreman mobile mode in a new tab — phone-optimised tap-to-tick checklist + work-history pad + Mark Completed button."><FileText size={13} />Foreman Mode</button>
          <div className="h-5 w-px bg-slate-200 self-center" />
          <button data-testid="generate-invoice-btn" onClick={generateInvoice} disabled={invoiceLoading} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-all disabled:opacity-50"><DollarSign size={13} />{invoiceLoading ? 'Generating...' : 'Generate Invoice'}</button>
          {/* Sprint 2 Order 4 — Generate/View POs buttons retired with the legacy purchase_orders pipeline. Materials ordering now flows through the Materials tab (recipe pipeline). */}
          <span className="mx-1 text-slate-300">|</span>
          <button data-testid="email-quote-button" onClick={() => setEmailOpen(true)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-purple-400 text-purple-600 hover:bg-purple-600 hover:text-white transition-all"><Mail size={13} />Email</button>
          <button data-testid="open-mobile-form" onClick={() => navigate(`/appraisals/${id}/mobile`)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-400 text-slate-600 hover:bg-slate-600 hover:text-white transition-all"><Smartphone size={13} />Field Form</button>
        </div>
      </div>
      <div className="flex gap-1 border-b border-[#E2E2DF] overflow-x-auto thin-scroll items-stretch">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            data-testid={`appraisal-tab-${t.key.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => switchTab(t.key)}
            title={t.label !== t.key ? `${t.label} (originally: ${t.key})` : t.key}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap relative ${tab === t.key ? 'border-[#D35400] text-[#D35400] font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200'}`}
          >{t.label}</button>
        ))}
        {/* Iter182++ — Customise tab order / labels. Sits at the end of
            the tab strip; opens a modal where the operator can re-order,
            rename, or hide tabs. Persists per-browser. */}
        <button
          data-testid="appraisal-tabs-customise-btn"
          onClick={() => setTabsCustomiserOpen(true)}
          title="Reorder tabs · rename · hide (saved to this browser)"
          className="ml-auto sticky right-0 px-2.5 py-2 my-1 mr-1 bg-white text-[#D35400] border border-[#D35400] hover:bg-[#D35400] hover:text-white rounded text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm"
        ><Settings2 size={13} /> Customise tabs</button>
      </div>
      <AppraisalTabsCustomiser
        open={tabsCustomiserOpen}
        onClose={() => setTabsCustomiserOpen(false)}
        defaultTabs={TABS}
        config={{ order: tabsCfg.order, labels: tabsCfg.labels, hidden: tabsCfg.hidden }}
        onPersist={tabsCfg.persist}
        onReset={tabsCfg.reset}
      />
      {tab === 'Overview' && (
        <div className="space-y-4">
          <PendingMaterialsCard api={api} appraisalId={id} />
          <CommunicationsLogPanel key={commsRefreshKey} api={api} appraisalId={id} />
          <SentQuotesPanel api={api} appraisalId={id} isAdmin={isAdmin} />
          <PropertyTenantAgentCard propertyId={data?.property_id} onChanged={loadData} />
          {/* RoofDiagramSlotsCard has moved to the new "Diagrams" tab to keep
              this Overview pane scannable. A quick jump-link is offered above
              via the stage-progress strip. */}
          <CompletionCertificateCard
            appraisalId={id}
            status={data?.appraisal_status}
            onOpenPdf={openPdfViewer}
          />
          <JobTrackingSheetCard
            appraisalId={id}
            onOpenPdf={openPdfViewer}
          />
          <QualitySignoffCard appraisalId={id} />
          <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Details</h3>
                {/* Wave 7 Path R (2026-02) — archive-state pill so the
                    operator instantly sees they're looking at a
                    historical (archived) appraisal. Clicking flips the
                    archive state so a returning client's old appraisal
                    can be re-activated. */}
                {data?.archived ? (
                  <span data-testid="appraisal-archived-pill" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-slate-700 text-white">
                    Archived
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {data?.archived ? (
                  <button
                    data-testid="unarchive-appraisal-button"
                    onClick={async () => {
                      try {
                        await api.post(`/appraisals/${id}/unarchive`);
                        toast.success('Appraisal un-archived — now visible in live pipeline');
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Un-archive failed'); }
                    }}
                    className="text-xs font-semibold px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors"
                    title="Re-activate this historical appraisal so it re-appears in the live pipeline + observability banners"
                  >Un-archive</button>
                ) : (
                  <button
                    data-testid="archive-appraisal-button"
                    onClick={async () => {
                      if (!window.confirm('Archive this appraisal? It stays visible on the client detail page but is hidden from default lists, dashboard, and observability banners.')) return;
                      try {
                        await api.post(`/appraisals/${id}/archive`);
                        toast.success('Appraisal archived');
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
                    }}
                    className="text-xs px-3 py-1.5 rounded border border-slate-400 text-slate-600 hover:bg-slate-700 hover:text-white hover:border-slate-700 transition-colors"
                    title="Archive this appraisal — hides from default lists + banners; keeps context on client detail page"
                  >Archive</button>
                )}
                <button data-testid="edit-appraisal-button" onClick={() => setEditing(!editing)} className="text-sm text-[#D35400] font-semibold hover:text-[#A04000]">{editing ? 'Cancel' : 'Edit'}</button>
              </div>
            </div>
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Appraiser</label>
                  <Combobox
                    value={form.appraisal_by || ''}
                    onChange={v => setForm({...form, appraisal_by: v})}
                    options={dropdowns.appraisers || []}
                    placeholder="Type a name…"
                    testId="detail-appraiser-combobox"
                    allowCustom={true}
                    addLabel="Add"
                    onAddNew={async (name) => {
                      const updated = await api.post('/dropdowns/appraisers/add', { value: name });
                      setDropdowns(prev => ({ ...prev, appraisers: Array.isArray(updated.data) ? updated.data : (prev.appraisers || []).concat(name) }));
                    }}
                  />
                </div>
                <div>
                  <QuotationTypeSelect api={api} roofType={(data?.roof_details || [])[0]?.roof_type || ''} current={form.quote_type_ref || ''}
                    disabled={!!buildConfirmed?.confirmed} testId="quote-type-edit" onPick={(ref) => setForm({ ...form, quote_type_ref: ref, quotation_type: ref })} />
                  {buildConfirmed?.confirmed && <p className="text-[11px] text-slate-500 mt-1">Components confirmed — click Revise on the Components tab to change.</p>}
                </div>
                {/* Roof Service Option — drives which builder/quote workflow
                    the operator gets (Reroof · New Roof · Repairs · Restoration). */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Service Option</label>
                  <ServiceOptionAndSubtypePicker
                    api={api}
                    serviceValue={data?.service_option_effective || form.service_option || 'Reroof'}
                    subtypeValue={form.roof_subtype || ''}
                    onServiceChange={(v) => setForm({ ...form, service_option: v })}
                    onSubtypeChange={(v) => setForm({ ...form, roof_subtype: v })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Markup</label>
                  <div data-testid="detail-markup-readonly" className="w-full px-3 py-2 border border-[#E2E2DF] bg-slate-50 rounded-md text-sm font-mono-num text-slate-700">
                    {((Number(summary?.markup ?? form.final_quote_markup ?? 1) - 1) * 100).toFixed(1)}%
                  </div>
                  <p className="text-[10px] text-slate-400 italic mt-1">Set in the Quote Summary below.</p>
                </div>
                {/* Contract Amount auto-syncs from Quote Summary Grand Total — not editable here. */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Contract Amount <span className="normal-case text-[10px] font-normal text-slate-400">(auto · = Grand Total)</span></label>
                  <div data-testid="detail-contract-amount-readonly" className="w-full px-3 py-2 border border-[#E2E2DF] bg-slate-50 rounded-md text-sm font-mono-num text-slate-700">
                    ${Number(form.contract_amount || summary?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {[
                  { key: 'job_days_expected', label: 'Days Expected', type: 'number' },
                  { key: 'contractor_name', label: 'Contractor' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">{f.label}</label>
                    <input type={f.type || 'text'} step={f.type === 'number' ? '0.01' : undefined} value={form[f.key] ?? ''} onChange={e => setForm({...form, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Discounts (applied to quote total)</label>
                  <div data-testid="detail-discounts-readonly" className="w-full px-3 py-2 border border-[#E2E2DF] bg-slate-50 rounded-md text-sm text-slate-700 flex flex-wrap items-center gap-2">
                    {(summary?.discounts || []).length
                      ? summary.discounts.map(d => <span key={d.key} className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-semibold">{d.label} {d.percent}%</span>)
                      : <span className="text-xs italic text-slate-400">No discount applied</span>}
                    <button type="button" data-testid="detail-discounts-edit-link"
                      onClick={() => document.querySelector('[data-testid="qs-discounts-editor"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      className="ml-auto text-xs font-semibold text-[#D35400] hover:underline">Edit in Quote Summary ↓</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">New Colour</label>
                  <select value={form.new_colour_choice ?? ''} onChange={e => setForm({...form, new_colour_choice: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option value="">Select...</option>
                    {/* 2026-02 Go-Live · roof-colour redesign — strip
                        leading whitespace on stored value, keep it in
                        label so brand names (Ebony, Karaka…) render
                        indented under their matching general tone. */}
                    {(dropdowns.roof_colours || []).map(c => (
                      <option key={c} value={typeof c === 'string' ? c.trim() : c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Insurance Co.</label>
                  <input value={form.insurance_co_name ?? ''} onChange={e => setForm({...form, insurance_co_name: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Appraisal Condition <span className="text-slate-400 normal-case font-normal">— powers "Repairs Detailed as Follows" on the quote</span></label>
                    <button
                      type="button"
                      data-testid="open-condition-picker-btn"
                      onClick={() => setShowConditionPicker(true)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-white border border-[#D35400] text-[#D35400] hover:bg-[#FFF7ED] rounded-md text-[11px] font-bold uppercase tracking-[0.12em]"
                    >
                      <Plus size={12} strokeWidth={2.5} /> Insert from Library
                    </button>
                  </div>
                  <textarea data-testid="appraisal-condition-input" value={form.appraisal_condition ?? ''} onChange={e => setForm({...form, appraisal_condition: e.target.value})} rows={8} placeholder="Paste or compose the repair narrative that appears on the customer quote. Use the 'Insert from Library' button to add standard phrases (re-bedding, flashings, restoration, etc.)." className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm leading-relaxed font-sans" />
                </div>
                <div className="md:col-span-2">
                  <InspectionCommentsPanel
                    api={api}
                    value={form.inspection_comments || {}}
                    onChange={(next) => setForm(f => ({ ...f, inspection_comments: next }))}
                    defaultRoofType={data?.property?.roof_details?.[0]?.roof_type || data?.roof_type || ''}
                    editing={true}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Internal Comments <span className="text-slate-400 normal-case font-normal">— private notes, not shown on the quote</span></label>
                  <textarea value={form.appraisal_comments ?? ''} onChange={e => setForm({...form, appraisal_comments: e.target.value})} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button data-testid="save-appraisal-button" onClick={saveAppraisal} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold"><Save size={14} /> Save Changes</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  ['Appraiser', data.appraisal_by],
                  ['Quote Type', data.quote_type_ref],
                  ['Markup', data.final_quote_markup ? `${(data.final_quote_markup * 100 - 100).toFixed(0)}%` : '-'],
                  ['Contract Amount', data.contract_amount ? `$${Number(data.contract_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'],
                  ['Days Expected', data.job_days_expected || '-'],
                  ['Contractor', data.contractor_name || '-'],
                  ['New Colour', data.new_colour_choice || '-'],
                  ['Insurance', data.insurance_co_name || '-'],
                ].map(([label, val]) => (
                  <div key={label}>
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
                    <p className="text-sm font-medium text-slate-800 mt-1">{val || '-'}</p>
                  </div>
                ))}
                {data.appraisal_condition && <div className="md:col-span-3"><span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Appraisal Condition <span className="text-slate-400 normal-case font-normal">— appears on customer quote</span></span><pre className="text-sm text-slate-700 mt-1 bg-[#FFF7ED]/60 border border-[#D35400]/20 p-3 rounded font-sans whitespace-pre-wrap leading-relaxed">{data.appraisal_condition}</pre></div>}
                {data.inspection_comments && Object.values(data.inspection_comments).some(v => (v || '').trim()) && (
                  <div className="md:col-span-3">
                    <InspectionCommentsPanel
                      api={api}
                      value={data.inspection_comments || {}}
                      defaultRoofType={data?.property?.roof_details?.[0]?.roof_type || data?.roof_type || ''}
                      editing={false}
                    />
                  </div>
                )}
                {data.appraisal_comments && <div className="md:col-span-3"><span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Internal Comments</span><p className="text-sm text-slate-600 mt-1 bg-slate-50 p-3 rounded">{data.appraisal_comments}</p></div>}
              </div>
            )}
            {summary && (
              <div className="border-t border-[#E2E2DF] pt-4" data-testid="quote-summary-panel">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-base font-semibold text-slate-800">Quote Summary</h4>
                    <UndoButton
                      history={pricingHistory}
                      currentGrand={Number(summary.grand_total || 0)}
                      currentIncGst={Number(summary.grand_total_inc_gst || 0)}
                      onUndo={undoPricing}
                    />
                    <UndoToolbar
                      appraisalId={id}
                      scope="pricing"
                      refreshKey={pricingHistory.length}
                      onRestored={() => { refreshPricingHistory(); loadData(); }}
                      testIdPrefix="pricing-undo"
                      compact={true}
                    />
                  </div>
                  <TargetGrandTotalInput
                    summary={summary}
                    onApply={(targetGrand) => {
                      const subtotal = Number(summary.subtotal || 0);
                      const discountFrac = (Number(summary.discount_pct || 0)) / 100.0;
                      if (subtotal <= 0 || targetGrand <= 0) return;
                      const idealMarkup = targetGrand / (subtotal * (1 - discountFrac));
                      if (idealMarkup >= 1.0) {
                        patchPricing({ final_quote_markup: idealMarkup, extra_discount_amount: 0 });
                        return;
                      }
                      const newExtra = Math.max(0, subtotal * (1 - discountFrac) - targetGrand);
                      patchPricing({ final_quote_markup: 1.0, extra_discount_amount: newExtra });
                    }}
                  />
                </div>

                {/* AUDIT #12 — Unpriced-lines banner. Surfaces when the
                    component sync couldn't resolve a rate for one or
                    more lines. Those lands as $0 in the total, so
                    without this banner the operator sees a suspiciously
                    low number without knowing why. */}
                {Number(summary.unpriced_count || 0) > 0 && (
                  <div
                    data-testid="qs-unpriced-banner"
                    className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  >
                    <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{summary.unpriced_count} line{summary.unpriced_count === 1 ? '' : 's'} couldn't be priced.</span>
                      {' '}They land as $0 in the total — check the Components tab and add rates for any missing component_type / material combos, then rebuild the quote.
                    </div>
                  </div>
                )}

                {/* Wave 2 / marine-zone auto-suggest — COLORSTEEL warning
                    banner + inline env override picker. Sits above the
                    Wave 4 anomaly stack because it's operator-context
                    (not a system anomaly) and gates product selection.
                    Renders as a subtle slate strip in inland/semi-marine
                    zones, escalates to rose (matching total_mismatch
                    styling) in marine/severe-marine so the COLORSTEEL
                    warning is impossible to miss. */}
                <EnvironmentZoneCard
                  appraisal={data}
                  api={api}
                  onUpdated={(next) => setData(next)}
                />

                {/* Wave 4 (2026-02) — 5 anomaly banners. Each fires
                    when the pipeline could silently produce a wrong
                    invoice. Same visual language as the unpriced_count
                    banner; operator learns one pattern and every future
                    silent-drop class piggy-backs on it. Backend flags
                    live under `summary.reconciliation`. */}
                {summary.reconciliation?.total_mismatch && (
                  <div data-testid="qs-total-mismatch-banner" className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-rose-500 bg-rose-50 px-3 py-2 text-xs text-rose-900">
                    <AlertCircle size={14} className="text-rose-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">Total mismatch:</span>{' '}
                      component_lines sum <strong className="font-mono-num">${Number(summary.reconciliation.total_mismatch.cl_sum).toFixed(2)}</strong>{' '}
                      ≠ quote_items sum <strong className="font-mono-num">${Number(summary.reconciliation.total_mismatch.qi_sum).toFixed(2)}</strong>{' '}
                      (Δ <strong className="font-mono-num">${Number(summary.reconciliation.total_mismatch.delta).toFixed(2)}</strong>).
                      {' '}Fix by hitting <em>Rebuild quote items</em> on the appraisal — the invoice is otherwise wrong.
                    </div>
                  </div>
                )}
                {summary.reconciliation?.zero_invoice && (
                  <div data-testid="qs-zero-invoice-banner" className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-rose-500 bg-rose-50 px-3 py-2 text-xs text-rose-900">
                    <AlertCircle size={14} className="text-rose-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">Zero-invoice anomaly:</span>{' '}
                      This appraisal has component_lines rows but the priced quote_items sum is $0.
                      {' '}Either every line failed to price (see the unpriced banner) or the sync helper skipped this appraisal. Trigger a rebuild.
                    </div>
                  </div>
                )}
                {Number(summary.reconciliation?.legacy_rows || 0) > 0 && (
                  <div data-testid="qs-legacy-rows-banner" className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{summary.reconciliation.legacy_rows} legacy row{summary.reconciliation.legacy_rows === 1 ? '' : 's'} in <code className="bg-amber-100 px-1 rounded">appraisal_components</code>.</span>
                      {' '}These are pre-gatekeeper writes that will NOT flow to invoices. Migrate to <code className="bg-amber-100 px-1 rounded">component_lines</code> or accept as historical noise (Wave 7 wipe-on-go-live target).
                    </div>
                  </div>
                )}
                {Number(summary.reconciliation?.blank_supplier_codes || 0) > 0 && (
                  <div data-testid="qs-blank-supplier-codes-banner" className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{summary.reconciliation.blank_supplier_codes} line{summary.reconciliation.blank_supplier_codes === 1 ? '' : 's'} with supplier but no supplier code.</span>
                      {' '}The purchase order will print with a blank code column for these lines. Set the code on each row's Materials tab or in the component_rate catalogue.
                    </div>
                  </div>
                )}
                {Number(summary.reconciliation?.orphan_cascade || 0) > 0 && (
                  <div data-testid="qs-orphan-cascade-banner" className="mb-3 flex items-start gap-2 rounded-md border-l-4 border-rose-500 bg-rose-50 px-3 py-2 text-xs text-rose-900">
                    <AlertCircle size={14} className="text-rose-700 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{summary.reconciliation.orphan_cascade} orphan cascade row{summary.reconciliation.orphan_cascade === 1 ? '' : 's'} detected.</span>
                      {' '}Auto-generated flashing rows point at a template that no longer exists. Delete the orphans or re-attach them to a live template.
                    </div>
                  </div>
                )}

                {/* Headline figures — Materials | Labour | Subtotal | Markup | Grand Total
                    Each financial cell carries its profit micro-row underneath in a lighter
                    font so the appraiser can spot under-priced lines at a glance. */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Materials */}
                  <div className="p-3 rounded bg-slate-50">
                    <span className="text-xs text-slate-500">Materials</span>
                    <p className="text-lg font-bold text-slate-900 font-mono-num">${Number(summary.total_materials || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[11px] mt-0.5 font-mono-num ${summary.materials_profit >= 0 ? 'text-emerald-500/80' : 'text-rose-500/80'}`} data-testid="qs-materials-profit">
                      Profit ${Number(summary.materials_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  {/* Labour */}
                  <div className="p-3 rounded bg-slate-50">
                    <span className="text-xs text-slate-500">Labour</span>
                    <p className="text-lg font-bold text-slate-900 font-mono-num">${Number(summary.total_labour || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[11px] mt-0.5 font-mono-num ${summary.labour_profit >= 0 ? 'text-emerald-500/80' : 'text-rose-500/80'}`} data-testid="qs-labour-profit">
                      Profit ${Number(summary.labour_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  {/* Subtotal — with combined profit underneath */}
                  <div className="p-3 rounded bg-slate-50">
                    <span className="text-xs text-slate-500">Subtotal</span>
                    <p className="text-lg font-bold text-slate-900 font-mono-num">${Number(summary.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className={`text-[11px] mt-0.5 font-mono-num font-semibold ${summary.total_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="qs-total-profit">
                      Total Profit ${Number(summary.total_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[11px] mt-0.5 font-mono-num text-slate-500" data-testid="qs-actual-margin">
                      Actual Margin <span className={`font-semibold ${summary.actual_margin_pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{Number(summary.actual_margin_pct || 0).toFixed(1)}%</span>
                    </p>
                  </div>
                  {/* Markup — INLINE EDITABLE */}
                  <div className="p-3 rounded bg-slate-50">
                    <span className="text-xs text-slate-500">Markup</span>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <input
                        type="number"
                        data-testid="qs-markup-input"
                        min={0}
                        max={200}
                        step={1}
                        value={Math.round((Number(summary.markup || 1) - 1) * 100)}
                        onChange={(e) => {
                          const pct = Math.max(0, Math.min(200, Number(e.target.value) || 0));
                          patchPricing({ final_quote_markup: 1 + pct / 100 });
                        }}
                        className="w-16 text-lg font-bold text-slate-900 bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono-num"
                      />
                      <span className="text-lg font-bold text-slate-900">%</span>
                    </div>
                    <p className="text-[11px] mt-0.5 text-slate-400 font-mono-num">+${Number((summary.marked_up_total || 0) - (summary.subtotal || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  {/* Grand Total — with GST breakdown underneath */}
                  <div className="p-3 rounded bg-[#D35400]/5 border border-[#D35400]/20">
                    <span className="text-xs text-[#A04000] font-semibold uppercase tracking-wider">Grand Total <span className="opacity-60 normal-case">excl. GST</span></span>
                    <p className="text-lg font-bold text-[#D35400] font-mono-num" data-testid="qs-grand-total">${Number(summary.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="text-[11px] mt-0.5 text-slate-500 font-mono-num">+ GST 15% ${Number(summary.gst_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className="text-[12px] mt-0.5 font-semibold text-slate-700 font-mono-num" data-testid="qs-grand-total-inc-gst">Incl. GST <span className="text-[#D35400]">${Number(summary.grand_total_inc_gst || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                  </div>
                </div>

                <PricingBreakdown summary={summary} />

                {/* Discounts — inline editable. Each checkbox toggles its slug
                    in the appraisal's discounts list and triggers a fresh
                    pricing PATCH so the panel updates instantly. */}
                <DiscountSummaryEditor
                  selected={summary.discounts?.map(d => d.key) || []}
                  totalPct={summary.discount_pct || 0}
                  totalAmount={summary.discount_amount || 0}
                  onChange={next => patchPricing({ discounts: next })}
                />

                {/* Target Margin pill row — clicking a target reverse-solves
                    the markup needed to hit that margin against the current
                    cost (total_buy_cost + total_contractor) and applies it
                    immediately. The "actual" pill on the left is locked and
                    reflects what the live quote is delivering. */}
                <TargetMarginRow
                  summary={summary}
                  onApply={(targetMargin) => {
                    const cost = Number(summary.total_cost || 0);
                    const subtotal = Number(summary.subtotal || 0);
                    const discountFrac = (Number(summary.discount_pct || 0)) / 100.0;
                    const extra = Number(summary.extra_discount_amount || 0);
                    if (subtotal <= 0 || targetMargin >= 100 || cost <= 0) return;
                    const targetGrand = cost / (1 - targetMargin / 100);
                    // Try Method A: adjust markup only (keep extra untouched).
                    // grand_total = subtotal × markup × (1-d) − extra
                    //   → markup = (targetGrand + extra) / (subtotal × (1-d))
                    const idealMarkup = (targetGrand + extra) / (subtotal * (1 - discountFrac));
                    if (idealMarkup >= 1.0) {
                      patchPricing({ final_quote_markup: idealMarkup });
                      return;
                    }
                    // Method B: markup can't go below 0% (1.0×) so absorb the
                    // gap into extra_discount instead. Keep markup at 1.0 and
                    // size extra to land exactly on targetGrand.
                    const newExtra = Math.max(0, subtotal * (1 - discountFrac) - targetGrand);
                    patchPricing({ final_quote_markup: 1.0, extra_discount_amount: newExtra });
                  }}
                />

                {/* Extra flat-dollar discount — applied AFTER the percentage
                    discounts so the appraiser can dial the final price down
                    to a target without having to compute the equivalent %. */}
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3" data-testid="qs-extra-discount-row">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Extra Discount ($)</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm font-bold text-slate-700">$</span>
                    <input
                      type="number"
                      data-testid="qs-extra-discount-input"
                      min={0}
                      step={10}
                      value={Math.round((Number(summary.extra_discount_amount || 0)) * 100) / 100}
                      onChange={(e) => {
                        const v = Math.max(0, Number(e.target.value) || 0);
                        patchPricing({ extra_discount_amount: v });
                      }}
                      className="w-28 text-sm font-bold text-slate-900 bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono-num"
                    />
                  </div>
                  {summary.extra_discount_amount > 0 && (
                    <button
                      type="button"
                      data-testid="qs-extra-discount-clear"
                      onClick={() => patchPricing({ extra_discount_amount: 0 })}
                      className="text-xs text-slate-500 hover:text-rose-600 underline"
                    >clear</button>
                  )}
                  <span className="ml-auto text-[11px] italic text-slate-500">Use to trim the final price without changing markup</span>
                </div>
              </div>
            )}
          </div>

          {/* Roof Details Section - inline on Appraisal page */}
          {data.property_id && (
            <div data-testid="appraisal-roof-section" className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 font-[Manrope]"><Home size={16} className="text-[#D35400]" /> Roof Details ({roofDetails.length})</h3>
                <div className="flex items-center gap-2">
                  <button data-testid="view-property-link" onClick={() => navigate(`/properties/${data.property_id}`)} className="text-xs text-slate-500 hover:text-[#D35400]">View Property</button>
                  <button data-testid="add-roof-from-appraisal" onClick={() => setNewRoof({ description: 'House', roof_type: '', roof_colour: '', roof_age: '', roof_brand: '', roof_pitch: '', roof_profile: '', roof_stories: 1, colour_condition: '', organic_growth: '', roof_state: '', roof_m2: 0, floor_m2: 0, ridge_length: 0, roof_angle: 0, extra_work_comments: '', extra_work_estimate: 0 })} className="flex items-center gap-1 text-sm text-[#D35400] font-semibold hover:text-[#A04000]"><Plus size={14} /> Add Roof</button>
                </div>
              </div>

              {newRoof && (
                <RoofForm roof={newRoof} setRoof={setNewRoof} onSave={addRoofDetail} onCancel={() => setNewRoof(null)} saveLabel="Save Roof" />
              )}

              {roofDetails.length === 0 && !newRoof ? (
                <p className="text-sm text-slate-400 text-center py-4">No roof details — add one to enable material filtering in the quote builder</p>
              ) : (
                <div className="space-y-3">
                  {roofDetails.map(r => editRoof?._id === r._id ? (
                    <RoofForm key={r._id} roof={editRoof} setRoof={setEditRoof} onSave={updateRoofDetail} onCancel={() => setEditRoof(null)} saveLabel="Update" />
                  ) : (
                    <RoofReadCard key={r._id} r={r} onEdit={() => setEditRoof({...r})} onDelete={() => deleteRoofDetail(r._id)} testIdSuffix="appraisal" />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {tab === 'Components' && (
        <div className="space-y-4" data-testid="components-tab-wrapper">
          <div className="flex justify-end"><PricingDrawer api={api} appraisalId={id} /></div>
          {/* Roof Details Adjustments — same live-edit bar used on the
              Builder, surfaced here so the operator can tweak Roof
              Type / Profile / Roof m² / Brand / Colour without leaving
              the Components view. */}
          <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid="components-roof-details-adjustments">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-sm font-bold text-slate-900">Roof Details Adjustments</h3>
              <div className="flex items-center gap-2">
                <a
                  href="/admin/roof-details-dropdowns"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="manage-roof-dropdowns-btn"
                  className="text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                >⚙ Manage dropdown lists ↗</a>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Live edit · same fields as Builder</span>
              </div>
            </div>
            <RoofDetailsQuickBar
              api={api}
              appraisal={roofPreview ? { ...(data || {}), ...roofPreview } : data}
              roofDetails={roofDetails}
              onRoofPreview={setRoofPreview}
              onApplied={loadData}
            />
          </div>
          {/* Ready-to-quote banner — final gate before generating the Quote PDF.
              Auto-refreshes when the user comes back to this tab. */}
          <ReadyToQuoteBanner
            api={api}
            appraisalId={id}
            refreshKey={data?.updated_at}
            onJumpTab={(t) => switchTab(t)}
            onGenerateQuote={() => switchTab('Quote Calculation')}
          />
          <AppraisalComponentsTab api={api} appraisalId={id} isAdmin={isAdmin} refreshKey={componentsRefreshKey} onJumpTab={(t) => switchTab(t)} />
        </div>
      )}
      {tab === 'Roof Appraisal' && <RoofAppraisalTab api={api} appraisalId={id} appraisal={data} roofs={roofDetails} onSaved={loadData} onJumpTab={(t) => switchTab(t)} />}
      {tab === 'Diagrams' && (
        <div className="space-y-4" data-testid="diagrams-tab">
          <RoofDiagramSlotsCard api={api} appraisalId={id} appraisal={data} onChanged={loadData} />
          {/* Big push-to-components action — re-runs the diagram-to-line-item
              sweep on demand. Operators are encouraged to hit this after any
              edit to the Master Roof Plan annotations. */}
          {!childBuilderOf(data) && <PushDiagramToComponentsButton api={api} appraisalId={id} onPushed={() => switchTab('Components')} />}
        </div>
      )}
      {tab === 'Builder' && (() => {
        // Default to the most-specific signal we have:
        //  1. operator's explicit pick (`builderSubtab` state)
        //  2. quote_type_ref keyword match (e.g. "Rescrew and Repair
        //     Longrun" → 'repairs', Iter211)
        //  3. broader service_option ("Repairs" → 'repairs')
        //  4. legacy default 'reroof'
        const activeSubtab =
          childBuilderOf(data)
          || builderSubtab
          || QUOTE_TYPE_REF_TO_SUBTAB(form.quote_type_ref)
          || SERVICE_TO_SUBTAB[form.service_option]
          || 'reroof';
        // Effective appraisal — folds in any pending roofPreview from
        // the QuickBar so the Restoration builder (which reads
        // `appraisal.roof_subtype` from its prop) hot-swaps to the
        // matching sub-builder live, before Apply is clicked.
        const effectiveAppraisal = roofPreview
          ? { ...(data || {}), ...roofPreview }
          : data;
        return (
          <div className="space-y-3" data-testid="builder-tab">
            <div className="flex items-center gap-3 flex-wrap border-b border-slate-200 pb-1">
              <div className="flex items-center gap-1">
                {subtabsFor(data).map((b) => (
                  <button
                    key={b.key}
                    data-testid={`builder-subtab-${b.key}`}
                    onClick={() => setBuilderSubtab(b.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                      activeSubtab === b.key
                        ? 'border-cyan-600 text-cyan-700 font-semibold'
                        : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200'
                    }`}
                  >
                    {b.label}
                    {SERVICE_TO_SUBTAB[form.service_option] === b.key && (
                      <span className="ml-1.5 text-[9px] uppercase font-bold tracking-wide text-orange-600">★ This appraisal</span>
                    )}
                  </button>
                ))}
              </div>
              {/* Iter167 — the shared Roof Details QuickBar above the
                  sub-tabs was removed per operator request. Every
                  builder now renders its own collapsible
                  <BuilderRoofDetailsPanel> right under its banner so
                  the field-picker + edit modes stay per-builder.
                  The QuickBar on the Components tab (below) is
                  unchanged. */}
            </div>
            <DraftsReminderBanner api={api} appraisalId={id} />
            {activeSubtab === 'reroof' && (
              <DraftBuilderGate api={api} appraisal={data} builder="reroof" onChanged={loadData}>
                {(bid) => <ReroofBuilderTab api={api} appraisalId={bid} refreshKey={builderRefreshKey} />}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'remedial_repairs' && (
              <RemedialRepairsBuilderTab
                api={api}
                appraisalId={id}
                appraisal={effectiveAppraisal}
                onRefresh={loadData}
                onJumpToPin={(pinId, pinType) => {
                  // Iter161 Phase B — annotator lives on the Overview
                  // tab in slot 'roof_plan'. Switch there, then fire
                  // either the focus-pin event (when a pin id is
                  // supplied) OR let any pending start-leak-pin-drop
                  // event (from RemedialRepairsBuilderTab) bubble
                  // through to the now-mounted annotator.
                  switchTab('Overview');
                  setTimeout(() => {
                    if (pinId) {
                      try { window.dispatchEvent(new CustomEvent('rtr:focus-pin', { detail: { pinId, pinType } })); } catch (_) { /* ignore */ }
                    }
                  }, 300);
                }}
              />
            )}
            {activeSubtab === 'repairs' && (
              <DraftBuilderGate api={api} appraisal={data} builder="repairs" onChanged={loadData}>
                {(bid, doc, reload) => (
                  <RepairsBuilderTab
                    api={api}
                    appraisalId={bid}
                    appraisal={reload ? { ...(doc || {}), ...(roofPreview || {}) } : effectiveAppraisal}
                    onRefresh={reload || loadData}
                    onOpenAnnotator={(pinTypeHint) => setAnnotatorPopOut({ pinTypeHint, from: 'repairs' })}
                  />
                )}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'painting' && (
              <DraftBuilderGate api={api} appraisal={data} builder="painting" onChanged={loadData}>
                {(bid, doc, reload) => (
                  <PaintingBuilderTab api={api} appraisalId={bid}
                    appraisal={reload ? { ...(doc || {}), ...(roofPreview || {}) } : effectiveAppraisal}
                    onRefresh={reload || loadData} />
                )}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'restoration' && (
              <RestorationBuilderTab
                api={api}
                appraisalId={id}
                appraisal={effectiveAppraisal}
                onRefresh={loadData}
                onOpenAnnotator={(pinTypeHint) => setAnnotatorPopOut({ pinTypeHint, from: 'restoration' })}
              />
            )}
            {activeSubtab === 'maintenance' && (
              <DraftBuilderGate api={api} appraisal={data} builder="maintenance" onChanged={loadData}>
                {(bid) => <MaintenanceBuilderTab api={api} appraisalId={bid} />}
              </DraftBuilderGate>
            )}
            {activeSubtab === 'guttering' && (
              <DraftBuilderGate api={api} appraisal={data} builder="guttering" onChanged={loadData}>
                {(bid) => <GutteringBuilderTab api={api} appraisalId={bid} />}
              </DraftBuilderGate>
            )}
          </div>
        );
      })()}
      {tab === 'Roof Details' && (
        <ConditionsTab data={data} form={form} setForm={setForm} dropdowns={dropdowns} roofDetails={roofDetails} editRoof={editRoof} setEditRoof={setEditRoof} newRoof={newRoof} setNewRoof={setNewRoof} addRoofDetail={addRoofDetail} updateRoofDetail={updateRoofDetail} deleteRoofDetail={deleteRoofDetail} saveAppraisal={saveAppraisal} navigate={navigate} propertyId={data?.property_id} />
      )}
      {tab === 'Quote Calculation' && (
        <QuoteCalculationTab appraisalId={id} api={api} />
      )}
      {tab === 'Quote Items' && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Quote Line Items</h3>
              {!isAdmin && <p className="text-xs text-slate-500 mt-0.5">Read-only · auto-synced from the Components tab</p>}
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <QuoteTemplatePicker api={api} appraisalId={id} onApplied={loadData} currentItemCount={(data.quote_items || []).length} />
                <button data-testid="add-from-catalog-button" onClick={() => setCatalogOpen(true)} className="flex items-center gap-1 text-sm text-green-600 font-semibold hover:text-green-700 border border-green-300 px-3 py-1.5 rounded-md hover:bg-green-50"><Package size={14} /> From Catalog</button>
                <button data-testid="add-quote-item-button" onClick={() => setNewItem({ item: '', ref: '', item_type: '', quantity: 0, length: 0, materials: 0, labour: 0, contractor: 0, state: '', requirements: '' })} className="flex items-center gap-1 text-sm text-[#D35400] font-semibold hover:text-[#A04000]"><Plus size={14} /> Manual</button>
              </div>
            )}
          </div>
          {!isAdmin && (
            <div data-testid="quote-items-readonly-banner" className="bg-blue-50/60 border border-blue-100 rounded-md p-3 text-xs text-blue-700 flex items-start gap-2">
              <Lock size={13} className="mt-0.5 flex-shrink-0" />
              <span>Quote Items are auto-generated from the <strong>Components</strong> tab. Only admins can manually edit lines here.</span>
            </div>
          )}

          {/* Components Summary Checklist */}
          {components.length > 0 && (
            <ComponentsSummary components={components} checkedComps={checkedComps} setCheckedComps={setCheckedComps} />
          )}

          {catalogOpen && (
            <CatalogPicker
              api={api}
              appraisalId={id}
              propertyId={data?.property_id}
              onAdd={() => { setCatalogOpen(false); loadData(); }}
              onClose={() => setCatalogOpen(false)}
            />
          )}

          {newItem && isAdmin && (
            <div className="border border-[#D35400] rounded-md p-4 bg-orange-50/30 space-y-3">
              <div className="mb-2">
                <label className="text-xs text-amber-700 font-semibold uppercase tracking-wider">Quick Fill from Product Catalog</label>
                <ProductAutoSuggest
                  api={api}
                  category={newItem.item}
                  placeholder={newItem.item ? `Search ${newItem.item} products...` : 'Search all products...'}
                  onSelect={(product) => {
                    setNewItem(prev => ({
                      ...prev,
                      item: prev.item || (product.category || '').charAt(0).toUpperCase() + (product.category || '').slice(1),
                      item_type: product.name || product.description || prev.item_type,
                      materials: product.materials_rate || prev.materials,
                      labour: product.labour_rate || prev.labour,
                      contractor: product.con_rate || prev.contractor,
                      state: product.supplier || prev.state,
                    }));
                  }}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Item Category</label>
                  <select data-testid="new-item-category" value={newItem.item || ''} onChange={e => setNewItem({...newItem, item: e.target.value, item_type: ''})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm">
                    <option value="">Select...</option>
                    {/* Iter258 — Display rename: Aquaseal → Dektite; value
                        stays "Aquaseal" so the saved item retains the
                        same component_type key downstream. */}
                    {[
                      'Sheet', 'Ridge', 'Valley', 'Barge', 'Spouting',
                      'Fastening', 'Flashing', 'Cleaning', 'Tiles',
                      'Safety', 'Travel', 'Urgent', 'Misc', 'Aquaseal',
                      'UnderLay',
                    ].map(c => (
                      <option key={c} value={c}>{c === 'Aquaseal' ? 'Dektite' : c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Ref</label>
                  <input type="text" placeholder="S1, R1" value={newItem.ref ?? ''} onChange={e => setNewItem({...newItem, ref: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-500">Description</label>
                  <DescriptionSelect
                    value={newItem.item_type || ''}
                    onChange={v => setNewItem({...newItem, item_type: v})}
                    options={itemDescriptions[newItem.item?.toLowerCase()] || []}
                    placeholder="Select or type description..."
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">State</label>
                  <input value={newItem.state ?? ''} onChange={e => setNewItem({...newItem, state: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Qty</label>
                  <input type="number" step="0.01" value={newItem.quantity ?? 0} onChange={e => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Length</label>
                  <input type="number" step="0.01" value={newItem.length ?? 0} onChange={e => setNewItem({...newItem, length: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Materials $</label>
                  <input type="number" step="0.01" value={newItem.materials ?? 0} onChange={e => setNewItem({...newItem, materials: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Labour $</label>
                  <input type="number" step="0.01" value={newItem.labour ?? 0} onChange={e => setNewItem({...newItem, labour: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Contractor $</label>
                  <input type="number" step="0.01" value={newItem.contractor ?? 0} onChange={e => setNewItem({...newItem, contractor: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Requirements <span className="text-slate-400">(click to add multiple)</span></label>
                <RequirementsSelect
                  value={newItem.requirements || ''}
                  onChange={v => setNewItem({...newItem, requirements: v})}
                  options={itemRequirements[newItem.item?.toLowerCase()] || []}
                  placeholder="Select or type requirements..."
                />
              </div>
              <div className="flex gap-2">
                <button data-testid="save-quote-item-button" onClick={addQuoteItem} className="px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-semibold hover:bg-[#A04000]">Save Item</button>
                <button onClick={() => setNewItem(null)} className="px-3 py-1.5 border border-[#E2E2DF] rounded text-sm hover:bg-slate-50">Cancel</button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  {['Ref', 'Item', 'Type', 'Qty', 'Length', 'Materials', 'Labour', 'Total', 'Supplier', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.quote_items || []).map(qi => editingItem?._id === qi._id ? (
                  <tr key={qi._id} className="border-t border-[#D35400] bg-orange-50/30">
                    <td className="px-2 py-1"><input value={editingItem.ref ?? ''} onChange={e => setEditingItem({...editingItem, ref: e.target.value})} className="w-full px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input value={editingItem.item ?? ''} onChange={e => setEditingItem({...editingItem, item: e.target.value})} className="w-full px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input value={editingItem.item_type ?? ''} onChange={e => setEditingItem({...editingItem, item_type: e.target.value})} className="w-full px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" value={editingItem.quantity ?? 0} onChange={e => setEditingItem({...editingItem, quantity: parseFloat(e.target.value) || 0})} className="w-20 px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" value={editingItem.length ?? 0} onChange={e => setEditingItem({...editingItem, length: parseFloat(e.target.value) || 0})} className="w-20 px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" value={editingItem.materials ?? 0} onChange={e => setEditingItem({...editingItem, materials: parseFloat(e.target.value) || 0})} className="w-24 px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" value={editingItem.labour ?? 0} onChange={e => setEditingItem({...editingItem, labour: parseFloat(e.target.value) || 0})} className="w-24 px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-3 py-2 font-semibold font-mono-num">${(Number(editingItem.materials || 0) + Number(editingItem.labour || 0)).toFixed(2)}</td>
                    <td className="px-2 py-1"><input value={editingItem.materials_supplier ?? editingItem.state ?? ''} onChange={e => setEditingItem({...editingItem, materials_supplier: e.target.value})} className="w-full px-1.5 py-1 border border-[#E2E2DF] rounded text-sm" /></td>
                    <td className="px-2 py-1">
                      <div className="flex gap-1">
                        <button data-testid="save-edit-item" onClick={updateQuoteItem} className="text-green-600 hover:text-green-700"><Save size={14} /></button>
                        <button onClick={() => setEditingItem(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={qi._id} className="border-t border-[#E2E2DF] hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium">{qi.ref}</td>
                    <td className="px-3 py-2">{qi.item}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={qi.item_type}>{qi.item_type}</td>
                    <td className="px-3 py-2 font-mono-num">{qi.quantity}</td>
                    <td className="px-3 py-2 font-mono-num">{qi.length || '-'}</td>
                    <td className="px-3 py-2 font-mono-num">${Number(qi.materials || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 font-mono-num">${Number(qi.labour || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 font-semibold font-mono-num">${Number(qi.total || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{qi.materials_supplier || qi.state || '-'}</td>
                    <td className="px-3 py-2">
                      {isAdmin ? (
                        <div className="flex gap-1">
                          <button data-testid={`edit-qi-${qi._id}`} onClick={() => setEditingItem({...qi})} className="text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                          <button onClick={() => deleteQuoteItem(qi._id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      ) : (
                        qi.import_source === 'component_sync' ? <span className="text-[10px] text-slate-400 uppercase tracking-wider">Synced</span> : null
                      )}
                    </td>
                  </tr>
                ))}
                {(data.quote_items || []).length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No quote items yet</td></tr>}
              </tbody>
              {(data.quote_items || []).length > 0 && (() => {
                const items = data.quote_items || [];
                const totMat = items.reduce((s, q) => s + Number(q.materials || 0), 0);
                const totLab = items.reduce((s, q) => s + Number(q.labour || 0), 0);
                const totAll = items.reduce((s, q) => s + Number(q.total || 0), 0);
                const markup = data.final_quote_markup || 1;
                const grandTotal = totAll * markup;
                return (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td colSpan={5} className="px-3 py-2 text-xs font-bold text-slate-500 text-right">Subtotal ({items.length} items)</td>
                      <td className="px-3 py-2 font-bold font-mono-num text-xs">${totMat.toFixed(2)}</td>
                      <td className="px-3 py-2 font-bold font-mono-num text-xs">${totLab.toFixed(2)}</td>
                      <td className="px-3 py-2 font-bold font-mono-num text-xs">${totAll.toFixed(2)}</td>
                      <td colSpan={2}></td>
                    </tr>
                    {markup > 1 && (
                      <tr className="bg-slate-50">
                        <td colSpan={7} className="px-3 py-2 text-xs font-bold text-slate-500 text-right">Markup ({((markup - 1) * 100).toFixed(0)}%)</td>
                        <td className="px-3 py-2 font-bold font-mono-num text-xs text-slate-500">+${(totAll * (markup - 1)).toFixed(2)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                    <tr className="border-t-2 border-[#D35400] bg-orange-50/40">
                      <td colSpan={7} className="px-3 py-2.5 text-sm font-bold text-[#D35400] text-right">Grand Total</td>
                      <td className="px-3 py-2.5 font-bold font-mono-num text-sm text-[#D35400]">${grandTotal.toFixed(2)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          </div>
        </div>
      )}
      {tab === 'Photos' && <PhotoGallery appraisalId={id} />}
      {tab === 'Booking' && (
        <BookingCard
          appraisalId={id}
          initialBooking={data?.appraisal_booking}
          appraisers={dropdowns.appraisers || []}
          variant="full"
          onSaved={(updated) => setData(d => ({ ...d, ...updated }))}
        />
      )}
      {tab === 'Job Tasks' && <JobTasksTab api={api} appraisalId={id} status={data?.appraisal_status} propertyId={data?.property_id} propertyData={data} reload={loadData} />}
      {tab === 'Work Sheets' && <WorkSheetsTab api={api} appraisalId={id} />}
      {tab === 'Quality' && <QualityTab api={api} appraisalId={id} />}
      {tab === 'Attachments' && <AttachmentsTab api={api} scope="appraisal" scopeId={id} />}
      {tab === 'Contractors' && <ContractorOverviewTab api={api} appraisalId={id} />}
      {tab === 'Materials' && <MaterialsTab api={api} appraisalId={id} />}
      {tab === 'Work Completion' && <WorkCompletionTab api={api} appraisalId={id} />}
      {tab === 'Exports' && <ExportsPanel api={api} appraisalId={id} appraisalTiers={data.proposal_tiers} clientEmail={client?.email || ''} onTiersUpdated={(t) => setData(d => ({ ...d, proposal_tiers: t }))} />}

      {/* Contractor Sheet contractor-picker prompt — shown when the
          operator clicks the Contractor Sheet PDF button but the
          appraisal has no primary contractor set. Friendlier than the
          backend's raw 400. */}
      {contractorPromptOpen && (
        <div
          data-testid="contractor-sheet-prompt"
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setContractorPromptOpen(false)}
        >
          <div
            className="bg-white rounded-md shadow-2xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-slate-900 font-[Manrope]">Pick a contractor</h3>
            <p className="text-xs text-slate-600">
              This appraisal doesn't have a primary contractor set yet. Pick one and we'll generate the Contractor Sheet PDF.
            </p>
            <select
              data-testid="contractor-sheet-prompt-select"
              value={contractorPromptId}
              onChange={(e) => setContractorPromptId(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">— Select contractor —</option>
              {contractors.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.contractor_name || c.company_name || '(unnamed)'}
                </option>
              ))}
            </select>
            <div className="flex flex-col gap-2 pt-1">
              <button
                data-testid="contractor-sheet-prompt-save-primary"
                onClick={() => confirmContractorSheet(true)}
                disabled={!contractorPromptId}
                className="bg-[#0F4F1B] text-white text-sm font-semibold rounded-md px-3 py-2 hover:bg-[#0a3a13] disabled:opacity-50"
              >
                Save as primary contractor &amp; open PDF
              </button>
              <button
                data-testid="contractor-sheet-prompt-just-open"
                onClick={() => confirmContractorSheet(false)}
                disabled={!contractorPromptId}
                className="bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-3 py-2 hover:bg-slate-50 disabled:opacity-50"
              >
                Just open PDF (don't save)
              </button>
              <button
                onClick={() => setContractorPromptOpen(false)}
                className="text-xs text-slate-500 hover:underline mt-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <EmailModal isOpen={emailOpen} onClose={() => setEmailOpen(false)} defaultTo={client?.email || ''} defaultSubject={`Roof Quotation - ${property?.property_address_num || ''} ${property?.property_address_street || ''}`} defaultBody={`Dear ${client?.name_first || ''} ${client?.name_last || ''},\n\nPlease find the details regarding your ${data?.quote_type_ref || 'roof'} quotation.\n\nKind regards,\nRoofs To Remedy Ltd`} appraisalId={id} />

      {/* Email Quote to Client — re-uses EmailModal with attach_pdf
          pre-ticked + pdf_type='quotation', so the salesperson can ship
          the live Quotation PDF (variant-compare baked in if the toggle
          is ON) in one click. Falls back to /email/log when SMTP isn't
          configured. */}
      <EmailModal
        isOpen={showEmailQuoteModal}
        onClose={() => setShowEmailQuoteModal(false)}
        defaultTo={client?.email || ''}
        defaultSubject={`Your roofing quotation — ${property?.property_address_num || ''} ${property?.property_address_street || ''}`.trim()}
        defaultBody={`Hi ${client?.name_first || ''},\n\nThanks for the opportunity to quote on your roof. Please find your Quotation PDF attached — totals, materials and a side-by-side comparison of the options we discussed are all in there for your kitchen-table review.\n\nLet me know which option you'd like to proceed with and I'll lock in a start date.\n\nNgā mihi,\nRoofs To Remedy Ltd`}
        appraisalId={id}
        defaultAttachPdf={true}
        defaultPdfType="quotation"
        markQuoteSent={true}
        onSent={() => loadData()}
      />

      {/* Go-Live Batch S · Send Quote Package modal — same EmailModal
          plumbing but attaches the merged Cover Letter + Company Overview
          + Quotation Letter PDF (pdf_type='quote-package'). Auto-flips
          appraisal_status to Quote Sent on successful SMTP send.
          "Also SMS client" tick surfaces when the client has an E.164
          mobile — fires POST /api/sms/send in parallel with the email. */}
      <EmailModal
        isOpen={showSendPackageModal}
        onClose={() => setShowSendPackageModal(false)}
        defaultTo={client?.email || ''}
        defaultSubject={`Your Roofs To Remedy Quote Package — ${property?.property_address_num || ''} ${property?.property_address_street || ''}`.trim()}
        defaultBody={`Hi ${client?.name_first || ''},\n\nThank you for the opportunity to inspect and quote on your roof. Please find your complete Quote Package attached — this includes:\n\n  • A cover letter\n  • An overview of Roofs To Remedy and how we work\n  • Your detailed Quotation with pricing and scope\n\nHave a read through at your convenience. Any questions at all, please just reply to this email or give us a call.\n\nNgā mihi,\nRoofs To Remedy Ltd`}
        appraisalId={id}
        defaultAttachPdf={true}
        defaultPdfType="quote-package"
        markQuoteSent={true}
        smsMobile={client?.phone_mobile_e164 || ''}
        smsDefaultMessage={`Hi ${client?.name_first || ''}, we've just emailed your Roofs To Remedy quote package for ${property?.property_address_num || ''} ${property?.property_address_street || ''}. Please check your inbox (and spam) — any questions, just reply here or call 021 616 739. Ngā mihi.`}
        onSent={() => { loadData(); setBuildConfirmationRefreshKey(k => k + 1); }}
      />

      {/* 2026-02 · Copy Email Script v1 — single read-only modal
          auto-picking the right script by appraisal_status. */}
      <EmailScriptModal
        appraisalId={id}
        open={showEmailScriptModal}
        onClose={() => setShowEmailScriptModal(false)}
      />

      {/* Page-level modals — mounted at the main component so they can read
          state set by the status-bar buttons above. Previously these lived
          inside QuoteCalculationTab and silently failed to open. */}
      <SendScriptModal
        open={showSendScript}
        onClose={() => { setShowSendScript(false); setCommsRefreshKey(k => k + 1); }}
        appraisalId={id}
        status={data?.appraisal_status || ''}
        property={data?.property || {}}
        agentLinked={!!data?.property?.real_estate_agent_id}
      />
      <ConditionScriptPicker
        open={showConditionPicker}
        onClose={() => setShowConditionPicker(false)}
        defaultRoofType={data?.property?.roof_details?.[0]?.roof_type || data?.roof_type || ''}
        onInsert={(text, mode) => {
          setForm(f => {
            const existing = (f.appraisal_condition || '').trim();
            const next = mode === 'replace' || !existing ? text : `${existing}\n\n${text}`;
            return { ...f, appraisal_condition: next };
          });
        }}
      />
      {/* Iter177 — universal Soft/Hard Delete modal. Opened by the
          rose Trash2 button next to the prev/next chevrons in the
          page header. On success we redirect to the list page. */}
      {deleteOpen && (
        <DeleteConfirmDialog
          kind="appraisal"
          entityId={id}
          label={data?.appraisal_no ? `A${data.appraisal_no}` : `appraisal ${id?.slice(-6) || ''}`}
          api={api}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => navigate('/appraisals')}
        />
      )}
      {/* Iter215 — Annotator Pop-Out Modal. Opened from a Component
          Header's "Edit pins on diagram" button inside the Repairs /
          Restoration builders. Wraps the SlotAnnotatorModal pointed at
          the Master Roof Plan slot (where builder pin items are sourced
          from). On close we reload the appraisal so the builder's pin
          rows reflect the operator's edits. `pinTypeHint` is surfaced in
          the modal heading so the operator knows which section they
          launched from. */}
      {annotatorPopOut && (
        <SlotAnnotatorModal
          open
          slotKey="roof_plan_template_id"
          slotLabel={annotatorPopOut.pinTypeHint
            ? `Roof Plan · editing ${annotatorPopOut.pinTypeHint} pins`
            : 'Roof Plan · edit pins'}
          slotColour="#D35400"
          appraisalId={id}
          templateId={data?.roof_plan_template_id || data?.roof_design_template_id || ''}
          roofPlanTemplateId={data?.roof_plan_template_id || data?.roof_design_template_id || ''}
          pins={data?.roof_diagram_annotations || []}
          roofProfile={(data?.roof_details || [])[0]?.roof_profile || ''}
          roofType={(data?.roof_details || [])[0]?.roof_type || ''}
          api={api}
          onPinsChange={(merged) => setData(d => ({ ...d, roof_diagram_annotations: merged }))}
          onClose={() => { setAnnotatorPopOut(null); loadData(); }}
        />
      )}
    </div>
  );
}

function WorkSheetsTab({ api, appraisalId }) {
  const [sheets, setSheets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { api.get(`/worksheets/${appraisalId}`).then(r => setSheets(r.data)).catch(() => {}); }, [api, appraisalId]);
  const save = async () => {
    await api.post('/worksheets', { ...form, appraisal_id: appraisalId });
    const { data } = await api.get(`/worksheets/${appraisalId}`);
    setSheets(data);
    setShowForm(false);
    setForm({});
  };
  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Work Sheets</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1 text-sm text-[#D35400] font-semibold"><Plus size={14} /> Add Day</button>
      </div>
      {showForm && (
        <div className="border border-[#E2E2DF] rounded p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[{ k: 'work_day_date', l: 'Date', t: 'date' }, { k: 'start_time', l: 'Start' }, { k: 'finish_time', l: 'Finish' }, { k: 'completed_by', l: 'Completed By' }, { k: 'status', l: 'Status' }, { k: 'temperature', l: 'Temp', t: 'number' }].map(f => (
              <div key={f.k}><label className="text-xs text-slate-500">{f.l}</label><input type={f.t || 'text'} value={form[f.k] ?? ''} onChange={e => setForm({...form, [f.k]: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" /></div>
            ))}
          </div>
          <div><label className="text-xs text-slate-500">Comments</label><textarea value={form.comments || ''} onChange={e => setForm({...form, comments: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" rows={2} /></div>
          <div className="flex gap-2"><button onClick={save} className="px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-semibold">Save</button><button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-[#E2E2DF] rounded text-sm">Cancel</button></div>
        </div>
      )}
      {sheets.length === 0 ? <p className="text-sm text-slate-400 py-4">No work sheet entries</p> : sheets.map(s => (
        <div key={s._id} className="p-3 border border-[#E2E2DF] rounded">
          <div className="flex items-center justify-between"><span className="text-sm font-medium">{s.work_day_date || 'N/A'}</span><span className="text-xs text-slate-500">{s.status}</span></div>
          {s.comments && <p className="text-xs text-slate-600 mt-1">{s.comments}</p>}
          <div className="flex gap-4 mt-1 text-xs text-slate-500">{s.start_time && <span>Start: {s.start_time}</span>}{s.finish_time && <span>Finish: {s.finish_time}</span>}{s.completed_by && <span>By: {s.completed_by}</span>}</div>
        </div>
      ))}
    </div>
  );
}

function QualityTab({ api, appraisalId }) {
  const [checks, setChecks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { api.get(`/quality-checks/${appraisalId}`).then(r => setChecks(r.data)).catch(() => {}); }, [api, appraisalId]);
  const save = async () => {
    await api.post('/quality-checks', { ...form, appraisal_id: appraisalId });
    const { data } = await api.get(`/quality-checks/${appraisalId}`);
    setChecks(data);
    setShowForm(false);
    setForm({});
  };
  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Quality Checks</h3>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1 text-sm text-[#D35400] font-semibold"><Plus size={14} /> Add Check</button>
      </div>
      {showForm && (
        <div className="border border-[#E2E2DF] rounded p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[{ k: 'quality_officer', l: 'Officer' }, { k: 'quality_check_date', l: 'Date', t: 'date' }, { k: 'quality_rating', l: 'Rating (1-10)', t: 'number' }].map(f => (
              <div key={f.k}><label className="text-xs text-slate-500">{f.l}</label><input type={f.t || 'text'} value={form[f.k] ?? ''} onChange={e => setForm({...form, [f.k]: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" /></div>
            ))}
          </div>
          <div><label className="text-xs text-slate-500">Comments</label><textarea value={form.quality_comments || ''} onChange={e => setForm({...form, quality_comments: e.target.value})} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" rows={2} /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.quality_pass || false} onChange={e => setForm({...form, quality_pass: e.target.checked})} /> Pass</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.action_required || false} onChange={e => setForm({...form, action_required: e.target.checked})} /> Action Required</label>
          </div>
          <div className="flex gap-2"><button onClick={save} className="px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-semibold">Save</button><button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-[#E2E2DF] rounded text-sm">Cancel</button></div>
        </div>
      )}
      {checks.length === 0 ? <p className="text-sm text-slate-400 py-4">No quality checks</p> : checks.map(c => (
        <div key={c._id} className={`p-3 border rounded ${c.quality_pass ? 'border-green-200 bg-green-50/50' : 'border-[#E2E2DF]'}`}>
          <div className="flex items-center justify-between"><span className="text-sm font-medium">{c.quality_officer}</span><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.quality_pass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.quality_pass ? 'PASS' : 'FAIL'}</span></div>
          <div className="flex gap-4 mt-1 text-xs text-slate-500">{c.quality_check_date && <span>{c.quality_check_date}</span>}<span>Rating: {c.quality_rating}/10</span></div>
          {c.quality_comments && <p className="text-xs text-slate-600 mt-1">{c.quality_comments}</p>}
        </div>
      ))}
    </div>
  );
}


function DescriptionSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const filtered = (options || []).filter(o => o.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="relative">
      <input
        data-testid="description-select-input"
        value={value}
        onChange={e => { onChange(e.target.value); setFilter(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-[#E2E2DF] rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.slice(0, 30).map(opt => (
            <button key={opt} onClick={() => { onChange(opt); setOpen(false); setFilter(''); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-[#E2E2DF] last:border-0">
              {opt}
            </button>
          ))}
        </div>
      )}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
    </div>
  );
}


const COMP_LABELS = {
  sheet: 'Sheet', tile: 'Tile', ridge: 'Ridge', valley: 'Valley', barge: 'Barge',
  spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing', cleaning: 'Cleaning',
  recoat: 'Re-Coat', aquaseal: 'Dektite', safety: 'Safety', travel: 'Travel',
  urgent: 'Urgent', misc: 'Misc', underlay: 'Underlay',
};

function ComponentsSummary({ components, checkedComps, setCheckedComps }) {
  const totalItems = components.length;
  const checkedCount = Object.values(checkedComps).filter(Boolean).length;

  const toggleCheck = (key) => {
    setCheckedComps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const checkAll = () => {
    const all = {};
    components.forEach((c, i) => { all[`${c.component_type}-${i}`] = true; });
    setCheckedComps(all);
  };

  const uncheckAll = () => setCheckedComps({});

  return (
    <div data-testid="components-summary" className="border border-blue-200 rounded-lg bg-blue-50/40 px-4 py-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-blue-800">Components</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">{checkedCount}/{totalItems}</span>
          {checkedCount === totalItems && totalItems > 0 && <span className="text-xs text-green-600 font-semibold">All Done</span>}
        </div>
        <div className="flex gap-2">
          <button data-testid="check-all-comps" onClick={checkAll} className="text-[10px] text-blue-600 hover:text-blue-800 font-medium">All</button>
          <button data-testid="uncheck-all-comps" onClick={uncheckAll} className="text-[10px] text-slate-400 hover:text-slate-600 font-medium">None</button>
        </div>
      </div>
      <div className="space-y-0.5">
        {components.map((c, i) => {
          const key = `${c.component_type}-${i}`;
          const checked = !!checkedComps[key];
          const parts = [
            (c.component_type || '').charAt(0).toUpperCase() + (c.component_type || '').slice(1),
            c.item_type,
            c.profile,
            c.quantity > 0 ? String(c.quantity) : '',
            c.length > 0 ? `${c.length}m` : '',
            c.brand,
            c.state,
          ].filter(Boolean);
          return (
            <label key={key} data-testid={`comp-check-${key}`} className={`flex items-center gap-2 cursor-pointer text-xs py-0.5 transition-colors ${checked ? 'text-green-600 line-through opacity-50' : 'text-slate-700'}`}>
              <input type="checkbox" checked={checked} onChange={() => toggleCheck(key)} className="w-3 h-3 rounded border-slate-300 text-blue-600 flex-shrink-0" />
              <span>{parts.join(', ')}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ConditionsTab({ data, form, setForm, dropdowns, roofDetails, editRoof, setEditRoof, newRoof, setNewRoof, addRoofDetail, updateRoofDetail, deleteRoofDetail, saveAppraisal, navigate, propertyId }) {
  // Iter171 — without a property_id, saves silently no-op. Disable
  // the Add button and surface a short explainer instead of letting
  // operators waste data entry on a form that won't persist.
  const noProperty = !propertyId;
  return (
    <div className="space-y-4">
      {/* Roof Details - editable */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 font-[Manrope]">Roof Details ({roofDetails.length})</h3>
          <div className="flex gap-2">
            {propertyId && <button onClick={() => navigate(`/properties/${propertyId}`)} className="text-xs text-slate-500 hover:text-[#D35400]">View Property</button>}
            <button
              data-testid="add-roof-conditions-tab"
              disabled={noProperty}
              title={noProperty ? 'Link a property to this appraisal first (see banner at top of page)' : ''}
              onClick={() => setNewRoof({ description: 'House', roof_type: '', roof_colour: '', roof_age: '', roof_brand: '', roof_pitch: '', roof_profile: '', material_type: '', roof_stories: 1, colour_condition: '', organic_growth: '', roof_state: '', roof_m2: 0, floor_m2: 0, ridge_length: 0, roof_angle: 0, extra_work_comments: '', extra_work_estimate: 0 })}
              className="flex items-center gap-1 text-sm text-[#D35400] font-semibold hover:text-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} /> Add Roof
            </button>
          </div>
        </div>

        {noProperty && (
          <div data-testid="conditions-tab-no-property-notice" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <strong>Linking required.</strong> This appraisal has no property attached yet — Roof Details can't save until you link one. Use the amber banner at the top of the page to find or create a property.
          </div>
        )}

        {newRoof && (
          <RoofForm roof={newRoof} setRoof={setNewRoof} onSave={addRoofDetail} onCancel={() => setNewRoof(null)} saveLabel="Save Roof" />
        )}

        {roofDetails.length === 0 && !newRoof && !noProperty && (
          <p className="text-sm text-slate-400 text-center py-4">No roof details added yet</p>
        )}

        {roofDetails.map(r => editRoof?._id === r._id ? (
          <RoofForm key={r._id} roof={editRoof} setRoof={setEditRoof} onSave={updateRoofDetail} onCancel={() => setEditRoof(null)} saveLabel="Update" />
        ) : (
          <RoofReadCard key={r._id} r={r} onEdit={() => setEditRoof({...r})} onDelete={() => deleteRoofDetail(r._id)} testIdSuffix="cond" />
        ))}
      </div>
    </div>
  );
}

// Shared read-only card showing every Roof Detail field as a labelled cell.
// Used by both the inline appraisal section and the Roof Details (Conditions) tab.
function RoofReadCard({ r, onEdit, onDelete, testIdSuffix }) {
  const stateBadgeClass =
    r.roof_state === 'Good' || r.roof_state === 'Excellent' ? 'bg-green-100 text-green-700'
    : r.roof_state === 'Poor' || r.roof_state === 'Very Poor' ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-700';

  const Cell = ({ label, value, mono }) => (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className={`text-slate-800 font-medium ${mono ? 'font-mono-num' : ''}`}>{value === '' || value === null || value === undefined ? '—' : value}</div>
    </div>
  );

  return (
    <div data-testid={`roof-read-card-${testIdSuffix}-${r._id}`} className="border border-[#E2E2DF] rounded-lg p-4 bg-white hover:border-slate-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-800">{r.description || 'Main Roof'} <span className="text-slate-400 font-normal">·</span> <span className="text-slate-600 font-medium">{r.roof_type || 'Unknown Type'}</span></h3>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${stateBadgeClass}`}>{r.roof_state || 'Not assessed'}</span>
          <button data-testid={`edit-roof-${testIdSuffix}-${r._id}`} onClick={onEdit} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
          <button data-testid={`delete-roof-${testIdSuffix}-${r._id}`} onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-xs">
        <Cell label="Description" value={r.description} />
        <Cell label="Roof Type" value={r.roof_type} />
        <Cell label="Brand" value={r.roof_brand} />
        <Cell label="Profile" value={r.roof_profile} />

        {/* Iter146 — Material Type (4th cascade tier, Iron-only).
            Always rendered to keep the 4-col grid aligned. Shows
            an amber IRON chip when populated on Iron roofs so the
            operator can spot which sheet material is targeted. */}
        <div data-testid={`roof-read-material-type-${r._id}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 flex items-center gap-1">
            <span>Material Type</span>
            {r.material_type && String(r.roof_type || '').trim().toLowerCase() === 'iron' && (
              <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-800 font-semibold">IRON</span>
            )}
          </div>
          <div className="text-slate-800 font-medium">{r.material_type || '—'}</div>
        </div>
        <Cell label="Pitch" value={r.roof_pitch} />
        <Cell label="Roof Angle" value={r.roof_angle ? `${r.roof_angle}°` : ''} mono />
        <Cell label="Stories" value={r.roof_stories ?? ''} mono />
        <Cell label="Colour" value={r.roof_colour} />

        <Cell label="Surface" value={r.colour_condition} />
        <Cell label="Growth" value={r.organic_growth} />
        <Cell label="Age" value={r.roof_age} />
        <Cell label="State" value={r.roof_state} />

        <Cell label="Floor m²" value={r.floor_m2 || 0} mono />
        <Cell label="Roof m²" value={r.roof_m2 || 0} mono />
        <Cell label="Ridge length" value={`${r.ridge_length || 0} m`} mono />
        <Cell label="Extra Work $" value={`$${Number(r.extra_work_estimate || 0).toLocaleString()}`} mono />
      </div>

      <div className="mt-3 pt-3 border-t border-[#F0EFEC]">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Extra Work Comments</div>
        {r.extra_work_comments
          ? <p className="text-xs text-slate-700 bg-amber-50/60 border border-amber-100 rounded px-2 py-1.5 whitespace-pre-wrap">{r.extra_work_comments}</p>
          : <p className="text-xs text-slate-400 italic">—</p>
        }
      </div>
    </div>
  );
}



function RequirementsSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const selected = value ? value.split(' | ').filter(Boolean) : [];
  const filtered = (options || []).filter(o => !selected.includes(o) && o.toLowerCase().includes(filter.toLowerCase()));

  const addReq = (req) => {
    const next = selected.length > 0 ? [...selected, req].join(' | ') : req;
    onChange(next);
    setFilter('');
  };

  const removeReq = (req) => {
    onChange(selected.filter(r => r !== req).join(' | '));
  };

  return (
    <div className="relative">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map(req => (
            <span key={req} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              {req.length > 60 ? req.slice(0, 60) + '...' : req}
              <button onClick={() => removeReq(req)} className="text-blue-400 hover:text-red-500 ml-0.5">&times;</button>
            </span>
          ))}
        </div>
      )}
      <input
        data-testid="requirements-select-input"
        value={filter}
        onChange={e => { setFilter(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={selected.length > 0 ? 'Add another...' : placeholder}
        className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-[#E2E2DF] rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.slice(0, 20).map(opt => (
            <button key={opt} onClick={() => { addReq(opt); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b border-[#E2E2DF] last:border-0">
              {opt}
            </button>
          ))}
        </div>
      )}
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
    </div>
  );
}



function JobTasksTab({ api, appraisalId, status, propertyId, propertyData, reload }) {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [taskCatalog, setTaskCatalog] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ task_name: '', category: '', contractor_id: '', contractor_name: '', rate: 0, unit: 'each', quantity: 1, scheduled_start: '', scheduled_end: '', notes: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: s }, { data: c }, { data: tc }] = await Promise.all([
        api.get(`/job-tasks/${appraisalId}`),
        api.get(`/job-tasks/summary/${appraisalId}`),
        api.get('/contractors'),
        api.get('/task-catalog?active=true'),
      ]);
      setTasks(t);
      setSummary(s);
      setContractors(c);
      setTaskCatalog(tc);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const autoCreate = async () => {
    try {
      await api.post(`/job-tasks/auto-create/${appraisalId}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, appraisal_id: appraisalId };
    if (editId) await api.put(`/job-tasks/${editId}`, payload);
    else await api.post('/job-tasks', payload);
    setShowForm(false); setEditId(null);
    setForm({ task_name: '', category: '', contractor_id: '', contractor_name: '', rate: 0, unit: 'each', quantity: 1, scheduled_start: '', scheduled_end: '', notes: '' });
    load();
  };

  const handleEdit = (t) => {
    setForm({ task_name: t.task_name, category: t.category || '', contractor_id: t.contractor_id || '', contractor_name: t.contractor_name || '', rate: t.rate || 0, unit: t.unit || 'each', quantity: t.quantity || 1, scheduled_start: t.scheduled_start || '', scheduled_end: t.scheduled_end || '', notes: t.notes || '' });
    setEditId(t._id);
    setShowForm(true);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const { data } = await api.patch(`/job-tasks/${taskId}/status`, { status: newStatus });
      if (data.all_tasks_completed) {
        if (window.confirm('All tasks are completed! Mark the entire job as "Completed"?')) {
          await api.patch(`/appraisals/${appraisalId}/status`, { appraisal_status: 'Completed', work_completed_date: new Date().toISOString().slice(0, 10), reason: 'All job tasks completed' });
        }
      }
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await api.delete(`/job-tasks/${id}`);
    load();
  };

  const onContractorChange = (contractorId) => {
    const c = contractors.find(x => x._id === contractorId);
    if (c) {
      setForm(prev => ({ ...prev, contractor_id: contractorId, contractor_name: c.contractor_name || '' }));
      // Try to find matching rate
      const taskName = form.task_name;
      if (taskName && c.rates) {
        const match = c.rates.find(r => r.task_name?.toLowerCase() === taskName.toLowerCase());
        if (match) setForm(prev => ({ ...prev, rate: match.rate || 0, unit: match.unit || prev.unit }));
        else if (c.default_hourly_rate) setForm(prev => ({ ...prev, rate: c.default_hourly_rate, unit: 'hour' }));
      }
    }
  };

  const onCatalogSelect = (taskId) => {
    const t = taskCatalog.find(x => x._id === taskId);
    if (t) {
      const updates = { task_name: t.name, category: t.category || '', rate: t.default_rate || form.rate, unit: t.default_unit || form.unit, task_catalog_id: t._id };
      // Auto-fill default contractor from catalog
      if (t.default_contractor_id) {
        const c = contractors.find(x => x._id === t.default_contractor_id);
        if (c) {
          updates.contractor_id = c._id;
          updates.contractor_name = c.contractor_name;
          // Check rate card for better rate
          const match = (c.rates || []).find(r => r.task_name?.toLowerCase() === t.name.toLowerCase());
          if (match) { updates.rate = match.rate || updates.rate; updates.unit = match.unit || updates.unit; }
        }
      }
      setForm(prev => ({ ...prev, ...updates }));
    }
  };

  const STATUS_BADGE = {
    'Draft': 'bg-slate-100 text-slate-600', 'Offered': 'bg-amber-100 text-amber-700',
    'Accepted': 'bg-blue-100 text-blue-700', 'Declined': 'bg-red-100 text-red-600',
    'Scheduled': 'bg-purple-100 text-purple-700', 'In Progress': 'bg-orange-100 text-orange-700',
    'Completed': 'bg-green-100 text-green-700',
  };

  const isJobAccepted = ['Accepted', 'Work Booked', 'Work In Progress', 'Completed', 'Invoiced', 'Paid'].includes(status);

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading tasks...</div>;

  return (
    <div data-testid="job-tasks-tab" className="space-y-4">
      {/* Rental status — captured at booking, BEFORE roof inspection.
          Persisted on the property doc so it carries through to the
          Work Sheet PDF & invoice letters. */}
      <RentalStatusInline api={api} propertyId={propertyId} onChanged={reload} />
      {/* On-site Extra Work editor (feeds the Work Sheet Page 2 + invoice totals) */}
      <ExtraWorkEditor appraisalId={appraisalId} />
      {/* Summary */}
      {summary && summary.task_count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            ['Tasks', summary.task_count, ''],
            ['Total Cost', `$${summary.total_task_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, ''],
            ['Owner Labour', `$${(summary.owner_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 'amber'],
            ['External Cost', `$${(summary.external_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, ''],
            ['Quoted Cost', `$${summary.quoted_contractor_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, ''],
            ['Variance', `$${summary.cost_variance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, summary.cost_variance > 0 ? 'red' : summary.cost_variance < 0 ? 'green' : ''],
          ].map(([label, val, color]) => (
            <div key={label} className={`p-3 rounded-lg border text-center ${color === 'red' ? 'bg-red-50 border-red-200' : color === 'green' ? 'bg-green-50 border-green-200' : color === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="text-xs text-slate-500">{label}</div>
              <div className="text-sm font-bold text-[#0F172A]">{val}</div>
              {label === 'Owner Labour' && <div className="text-[10px] text-amber-600 mt-0.5">= Retained Profit</div>}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {isJobAccepted && tasks.length === 0 && (
          <button data-testid="auto-create-tasks-btn" onClick={autoCreate} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            <ClipboardList size={14} /> Auto-Create from Quote Items
          </button>
        )}
        <button data-testid="add-job-task-btn" onClick={() => { setShowForm(true); setEditId(null); setForm({ task_name: '', category: '', contractor_id: '', contractor_name: '', rate: 0, unit: 'each', quantity: 1, scheduled_start: '', scheduled_end: '', notes: '' }); }} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] transition-colors">
          <Plus size={14} /> Add Task
        </button>
        {!isJobAccepted && <span className="text-xs text-slate-400">Job tasks are typically added after the quote is accepted.</span>}
      </div>

      {/* Form */}
      {showForm && (
        <div data-testid="job-task-form" className="bg-white border-2 border-[#D35400] rounded-xl p-4">
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Task (from catalog)</label>
              <select data-testid="task-catalog-select" value="" onChange={e => onCatalogSelect(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Pick from catalog...</option>
                {taskCatalog.map(t => <option key={t._id} value={t._id}>{t.name} ({t.category})</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Task Name *</label>
              <input data-testid="job-task-name" required value={form.task_name} onChange={e => setForm({ ...form, task_name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g. Strip and reroof main section" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Contractor</label>
              <select data-testid="job-task-contractor" value={form.contractor_id} onChange={e => onContractorChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="">Unassigned</option>
                {contractors.map(c => <option key={c._id} value={c._id}>{c.contractor_name}{c.company_name ? ` (${c.company_name})` : ''}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Rate ($)</label>
                <input type="number" step="0.01" value={form.rate} onChange={e => setForm({ ...form, rate: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Unit</label>
                <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                  {['m2', 'lm', 'hour', 'each', 'fixed'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Qty</label>
                <input type="number" step="0.01" value={form.quantity} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Total: ${((form.rate || 0) * (form.quantity || 0)).toFixed(2)}</label>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
              <input type="date" value={form.scheduled_start} onChange={e => setForm({ ...form, scheduled_start: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
              <input type="date" value={form.scheduled_end} onChange={e => setForm({ ...form, scheduled_end: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div className="md:col-span-3 flex gap-2">
              <button data-testid="save-job-task-btn" type="submit" className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]"><Save size={14} /> {editId ? 'Update' : 'Create'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Task List */}
      {tasks.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <ClipboardList size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">{isJobAccepted ? 'No tasks yet. Click "Auto-Create from Quote Items" or add manually.' : 'Tasks can be added once the quote is accepted.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-4 py-2.5">#</th>
                <th className="text-left px-4 py-2.5">Task</th>
                <th className="text-left px-4 py-2.5">Contractor</th>
                <th className="text-right px-4 py-2.5">Rate</th>
                <th className="text-right px-4 py-2.5">Qty</th>
                <th className="text-right px-4 py-2.5">Cost</th>
                <th className="text-left px-4 py-2.5">Schedule</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map((t, idx) => (
                <tr key={t._id} data-testid={`job-task-row-${t._id}`} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 text-slate-400">{idx + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-[#0F172A]">{t.task_name}</div>
                    {t.category && <div className="text-xs text-slate-400">{t.category}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {t.contractor_name || <span className="text-slate-300 italic">Unassigned</span>}
                    {t.is_owner_task && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded-full font-bold">OWNER</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">${(t.rate || 0).toFixed(2)}/{t.unit}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{t.quantity}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-medium">${(t.total_cost || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">
                    {t.scheduled_start ? `${t.scheduled_start}${t.scheduled_end ? ` - ${t.scheduled_end}` : ''}` : <span className="text-slate-300">Not scheduled</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      data-testid={`task-status-${t._id}`}
                      value={t.status}
                      onChange={e => handleStatusChange(t._id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${STATUS_BADGE[t.status] || 'bg-slate-100'}`}
                    >
                      {['Draft', 'Offered', 'Accepted', 'Declined', 'Scheduled', 'In Progress', 'Completed'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(t)} className="p-1 text-slate-400 hover:text-[#D35400] rounded"><Edit2 size={13} /></button>
                      <button onClick={() => handleDelete(t._id)} className="p-1 text-slate-400 hover:text-red-500 rounded"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


const CALC_COMPONENTS = [
  { key: 'sheet', label: 'Sheets', hasLength: true },
  { key: 'tile', label: 'Tiles', hasLength: false },
  { key: 'ridge', label: 'Ridges', hasLength: true },
  { key: 'valley', label: 'Valleys', hasLength: true },
  { key: 'barge', label: 'Barges', hasLength: true },
  { key: 'spouting', label: 'Spouting', hasLength: true },
  { key: 'flashing', label: 'Flashings', hasLength: true },
  { key: 'fastening', label: 'Fastenings', hasLength: false },
  { key: 'aquaseal', label: 'Dektite', hasLength: false },
  { key: 'cleaning', label: 'Cleaning', hasLength: false },
  { key: 'recoat', label: 'Re-Coat', hasLength: false },
  { key: 'safety', label: 'Safety', hasLength: false },
  { key: 'underlay', label: 'Underlay', hasLength: false },
  { key: 'urgent', label: 'Urgent', hasLength: false },
  { key: 'misc', label: 'Misc', hasLength: false },
  { key: 'travel', label: 'Travel', hasLength: false },
];

function QuoteCalculationTab({ appraisalId, api }) {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Read from the NEW component_lines endpoint (single source of truth).
    // Each line stores its rates inline so no rate-table lookup is needed.
    api.get(`/appraisals/${appraisalId}/component-lines`)
      .then(r => {
        const groups = r.data?.groups || {};
        const flat = [];
        Object.entries(groups).forEach(([compType, lines]) => {
          (lines || []).forEach(l => {
            flat.push({
              ...l,
              // Match legacy lower-case keys used by CALC_COMPONENTS
              component_type: String(compType || '').toLowerCase(),
              item_type: l.type_name || 'Unspecified',
            });
          });
        });
        setComponents(flat);
      })
      .catch(() => setComponents([]))
      .finally(() => setLoading(false));
  }, [api, appraisalId]);

  const grouped = {};
  components.forEach(c => {
    const type = c.component_type || 'misc';
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(c);
  });

  // Every line type is shown — types outside the fixed list (Extra Painting, Labour, Pointing…)
  // get their own section so the tab always totals the same as Quote Items.
  const knownKeys = new Set(CALC_COMPONENTS.map(c => c.key));
  const extraSections = Object.keys(grouped).filter(k => !knownKeys.has(k)).sort().map(k => ({
    key: k, label: k.replace(/\b\w/g, ch => ch.toUpperCase()), hasLength: grouped[k].some(it => parseFloat(it.length) > 0),
  }));
  const calcResults = [...CALC_COMPONENTS, ...extraSections].map(cc => {
    const items = grouped[cc.key] || [];
    if (items.length === 0) return null;

    const byType = {};
    items.forEach(it => {
      const key = it.item_type || 'Unspecified';
      if (!byType[key]) byType[key] = { items: [], profile: it.profile || '' };
      byType[key].items.push(it);
    });

    const subRows = Object.entries(byType).map(([typeName, group]) => {
      let total = 0;
      let totalQty = 0;
      let totalLength = 0;
      // Snapshot rates from the first line (rates are stored per-line; if
      // they differ we still display the first; profit math sums per line).
      const first = group.items[0] || {};
      const rates = {
        buy_cost: parseFloat(first.buy_cost) || 0,
        materials_rate: parseFloat(first.materials_rate) || 0,
        labour_rate: parseFloat(first.labour_rate) || 0,
        contractor_rate: parseFloat(first.contractor_rate) || 0,
      };
      let buyTotal = 0, matTotal = 0, labTotal = 0, conTotal = 0, profit = 0;
      group.items.forEach(it => {
        const qty = parseFloat(it.quantity) || 0;
        const len = parseFloat(it.length) || 0;
        const lineTotal = len > 0 ? qty * len : qty;
        totalQty += qty;
        totalLength += len;
        total += lineTotal;
        const b = parseFloat(it.buy_cost) || 0;
        const m = parseFloat(it.materials_rate) || 0;
        const l = parseFloat(it.labour_rate) || 0;
        const c = parseFloat(it.contractor_rate) || 0;
        buyTotal += lineTotal * b;
        matTotal += lineTotal * m;
        labTotal += lineTotal * l;
        conTotal += lineTotal * c;
        profit  += lineTotal * ((m - b) + (l - c));
      });
      return {
        typeName, profile: group.profile, count: group.items.length,
        totalQty, totalLength, total, hasLength: cc.hasLength,
        rates,
        buyTotal: Math.round(buyTotal * 100) / 100,
        matTotal: Math.round(matTotal * 100) / 100,
        labTotal: Math.round(labTotal * 100) / 100,
        conTotal: Math.round(conTotal * 100) / 100,
        rtrTotal: Math.round((matTotal + labTotal) * 100) / 100,
        profit:   Math.round(profit   * 100) / 100,
      };
    });

    const grandTotal = subRows.reduce((s, r) => s + r.total, 0);
    const grandBuy = subRows.reduce((s, r) => s + r.buyTotal, 0);
    const grandMat = subRows.reduce((s, r) => s + r.matTotal, 0);
    const grandLab = subRows.reduce((s, r) => s + r.labTotal, 0);
    const grandCon = subRows.reduce((s, r) => s + r.conTotal, 0);
    const grandRtr = Math.round((grandMat + grandLab) * 100) / 100;
    const grandProfit = subRows.reduce((s, r) => s + r.profit, 0);
    return { ...cc, subRows, grandTotal, grandBuy, grandMat, grandLab, grandCon, grandRtr, grandProfit, itemCount: items.length };
  }).filter(Boolean);

  const overallBuy = calcResults.reduce((s, r) => s + r.grandBuy, 0);
  const overallMat = calcResults.reduce((s, r) => s + r.grandMat, 0);
  const overallLab = calcResults.reduce((s, r) => s + r.grandLab, 0);
  const overallCon = calcResults.reduce((s, r) => s + r.grandCon, 0);
  const overallRtr = Math.round((overallMat + overallLab) * 100) / 100;
  const overallProfit = calcResults.reduce((s, r) => s + r.grandProfit, 0);

  if (loading) return <div className="bg-white border border-[#E2E2DF] rounded-md p-6 text-center text-slate-400">Loading...</div>;

  if (calcResults.length === 0) return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-12 text-center">
      <p className="text-sm text-slate-500">No components entered yet.</p>
      <p className="text-xs text-slate-400 mt-1">Add components in the Components tab to see calculations here.</p>
    </div>
  );

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Quote Calculation Summary</h3>
        <div className="flex gap-4 text-xs">
          <span className="text-slate-500">Buy: <span className="font-bold text-slate-800">${overallBuy.toFixed(2)}</span></span>
          <span className="text-slate-500">Materials: <span className="font-bold text-green-700">${overallMat.toFixed(2)}</span></span>
          <span className="text-slate-500">Labour: <span className="font-bold text-blue-700">${overallLab.toFixed(2)}</span></span>
          <span className="text-slate-500">Contractor: <span className="font-bold text-purple-700">${overallCon.toFixed(2)}</span></span>
          <span className="text-slate-500">RTR Profit: <span className="font-bold text-green-800">${overallProfit.toFixed(2)}</span></span>
          <span className="text-slate-500">RTR Total: <span className="font-bold text-orange-700">${overallRtr.toFixed(2)}</span></span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {calcResults.map(cr => (
          <div key={cr.key} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
              <span className="text-sm font-bold text-slate-700">{cr.label}</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ minWidth: 1000, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '7%' }} />
              </colgroup>
              <thead>
                <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-left px-2 py-1.5">Profile</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-right px-2 py-1.5">Buy $</th>
                  <th className="text-right px-2 py-1.5 bg-slate-50">Buy Total</th>
                  <th className="text-right px-2 py-1.5">Mat $</th>
                  <th className="text-right px-2 py-1.5 bg-green-50/50">Mat Total</th>
                  <th className="text-right px-2 py-1.5">Lab $</th>
                  <th className="text-right px-2 py-1.5 bg-blue-50/50">Lab Total</th>
                  <th className="text-right px-2 py-1.5">Con $</th>
                  <th className="text-right px-2 py-1.5 bg-purple-50/50">Con Total</th>
                  <th className="text-right px-2 py-1.5 bg-green-100/50">RTR Profit</th>
                  <th className="text-right px-2 py-1.5 bg-orange-50/50">RTR Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cr.subRows.map((sr, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-2 py-1.5 text-slate-700 font-medium truncate" title={sr.typeName}>{sr.typeName}</td>
                    <td className="px-2 py-1.5 text-slate-500 truncate">{sr.profile || '-'}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800">{cr.hasLength ? sr.total.toFixed(1) : sr.total}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-400">{sr.rates.buy_cost.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-700 bg-slate-50/50">${sr.buyTotal.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-400">{sr.rates.materials_rate.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-green-700 bg-green-50/30">${sr.matTotal.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-400">{sr.rates.labour_rate.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-blue-700 bg-blue-50/30">${sr.labTotal.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-400">{sr.rates.contractor_rate.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-purple-700 bg-purple-50/30">${sr.conTotal.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-green-800 bg-green-100/30">${sr.profit.toFixed(2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-orange-700 bg-orange-50/30">${sr.rtrTotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={2} className="px-2 py-2 text-right text-xs font-bold text-slate-500">{cr.label} Total</td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-[#D35400]">{cr.hasLength ? cr.grandTotal.toFixed(1) : cr.grandTotal}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-slate-700">${cr.grandBuy.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-green-700">${cr.grandMat.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-blue-700">${cr.grandLab.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-purple-700">${cr.grandCon.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-green-800">${cr.grandProfit.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-orange-700">${cr.grandRtr.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        ))}

        {/* Grand Subtotal */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
            <span className="text-sm font-bold text-slate-700">Summary</span>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ minWidth: 1000, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
            </colgroup>
            <thead>
              <tr className="text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-200">
                <th className="text-left px-2 py-1.5">Component</th>
                <th className="text-right px-2 py-1.5"></th>
                <th className="text-right px-2 py-1.5">Total</th>
                <th className="text-right px-2 py-1.5"></th>
                <th className="text-right px-2 py-1.5">Buy Total</th>
                <th className="text-right px-2 py-1.5"></th>
                <th className="text-right px-2 py-1.5">Mat Total</th>
                <th className="text-right px-2 py-1.5"></th>
                <th className="text-right px-2 py-1.5">Lab Total</th>
                <th className="text-right px-2 py-1.5"></th>
                <th className="text-right px-2 py-1.5">Con Total</th>
                <th className="text-right px-2 py-1.5 bg-green-100/50">RTR Profit</th>
                <th className="text-right px-2 py-1.5 bg-orange-50/50">RTR Total</th>
              </tr>
            </thead>
            <tbody>
              {calcResults.map(cr => (
                <tr key={cr.key} className="border-b border-slate-200 hover:bg-slate-50">
                  <td colSpan={2} className="px-2 py-2 text-sm font-semibold text-slate-700">{cr.label}</td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-bold text-slate-600">{cr.hasLength ? cr.grandTotal.toFixed(1) : cr.grandTotal}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-semibold text-slate-700">${cr.grandBuy.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-semibold text-green-700">${cr.grandMat.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-semibold text-blue-700">${cr.grandLab.toFixed(2)}</td>
                  <td></td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-semibold text-purple-700">${cr.grandCon.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-bold text-green-800 bg-green-50/30">${cr.grandProfit.toFixed(2)}</td>
                  <td className="px-2 py-2 text-right font-mono text-xs font-bold text-orange-700 bg-orange-50/30">${cr.grandRtr.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-400 bg-slate-100">
                <td colSpan={2} className="px-2 py-3 text-sm font-bold text-slate-800">GRAND TOTAL</td>
                <td></td>
                <td></td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-slate-800">${overallBuy.toFixed(2)}</td>
                <td></td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-green-700">${overallMat.toFixed(2)}</td>
                <td></td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-blue-700">${overallLab.toFixed(2)}</td>
                <td></td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-purple-700">${overallCon.toFixed(2)}</td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-green-800 bg-green-100/50">${overallProfit.toFixed(2)}</td>
                <td className="px-2 py-3 text-right font-mono text-sm font-bold text-orange-700 bg-orange-50/50">${overallRtr.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -- DiscountSummaryEditor ----------------------------------------------- */
/* Inline-editable discount list used inside the Quote Summary panel.       */
/* Toggles each discount slug and fires onChange with the updated array;    */
/* the panel's parent then PATCHes /appraisals/{id}/pricing.                */
function DiscountSummaryEditor({ selected, totalPct, totalAmount, onChange }) {
  const { api } = useAuth();
  const [catalogue, setCatalogue] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/discount-catalogue')
      .then(r => { if (alive) setCatalogue(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  const toggle = (key) => {
    const has = selected.includes(key);
    onChange(has ? selected.filter(k => k !== key) : [...selected, key]);
  };

  return (
    <div data-testid="qs-discounts-editor" className="mt-4 bg-amber-50/60 border border-amber-100 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">Discounts — toggle to apply</span>
        {totalPct > 0 ? (
          <span className="text-xs font-mono-num font-bold text-amber-800" data-testid="qs-discount-total">
            −${Number(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({totalPct}%)
          </span>
        ) : (
          <span className="text-xs italic text-amber-700/60">no discount applied</span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
        {catalogue.map(d => {
          const active = selected.includes(d.key);
          return (
            <label
              key={d.key}
              data-testid={`qs-discount-${d.key}`}
              className={`flex items-center gap-2 px-2 py-1.5 border rounded-md cursor-pointer text-xs transition-colors ${
                active
                  ? 'bg-[#FFF7ED] border-[#D35400] text-[#A04000]'
                  : 'bg-white border-amber-200 text-slate-700 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggle(d.key)}
                className="accent-[#D35400] w-3 h-3"
              />
              <span className="flex-1 truncate">{d.label}</span>
              <span className="font-mono-num text-[10px] font-semibold opacity-80">{d.percent}%</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}


/* -- TargetMarginRow ---------------------------------------------------- */
/* Pill bar of target margin percentages (0/5/10/.../50). Each pill shows  */
/* the projected Grand Total (excl. GST) that would be achieved if you     */
/* picked that target — using the appraisal's current cost, subtotal, and  */
/* percentage discounts. Click to reverse-solve the markup and PATCH.      */
const TARGET_MARGIN_PCTS = [0, 10, 20, 30, 40, 50];

function TargetMarginRow({ summary, onApply }) {
  const cost = Number(summary.total_cost || 0);
  const subtotal = Number(summary.subtotal || 0);
  const discountFrac = (Number(summary.discount_pct || 0)) / 100.0;
  const extra = Number(summary.extra_discount_amount || 0);
  const actual = Number(summary.actual_margin_pct || 0);

  // For each target M, project the grand_total that would be achieved.
  // grand_total = cost / (1 - M).
  const project = (M) => {
    if (M >= 100) return Infinity;
    if (cost <= 0) return 0;
    return cost / (1 - M / 100);
  };

  // Identify the closest target pill to the current actual margin so the
  // appraiser can spot which one matches.
  const closest = TARGET_MARGIN_PCTS.reduce(
    (best, p) => (Math.abs(p - actual) < Math.abs(best - actual) ? p : best),
    TARGET_MARGIN_PCTS[0],
  );
  const canSolve = subtotal > 0 && cost > 0;

  return (
    <div className="mt-4 bg-emerald-50/40 border border-emerald-100 rounded-md p-3" data-testid="qs-target-margin-row">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Target Margin · click to apply</span>
        <span className="text-[11px] text-emerald-700 font-semibold" data-testid="qs-actual-margin-inline">
          Actual margin <span className="font-mono-num text-emerald-900">{actual.toFixed(1)}%</span>
          {cost > 0 && <span className="text-emerald-600/70 font-normal ml-1">(cost ${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>}
        </span>
      </div>
      {Number(summary.uncosted_count) > 0 && (
        <p data-testid="qs-uncosted-warning" className="text-[11px] font-semibold text-amber-800 bg-amber-50 ring-1 ring-amber-300 rounded px-2 py-1 mb-2">
          {summary.uncosted_count} line{summary.uncosted_count === 1 ? ' has' : 's have'} no buy-in or contractor cost, so the margin is overstated.
        </p>
      )}
      {!canSolve && (
        <p className="text-[11px] italic text-emerald-700/60">Add Quote Items first — target margin needs cost &amp; subtotal to reverse-solve.</p>
      )}
      {canSolve && (
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-11 gap-1.5">
          {TARGET_MARGIN_PCTS.map(p => {
            const projected = project(p);
            const isClosest = p === closest;
            return (
              <button
                key={p}
                type="button"
                data-testid={`qs-target-margin-${p}`}
                title={`Apply target margin ${p}% → reverse-solve markup`}
                onClick={() => onApply(p)}
                className={`flex flex-col items-center px-2 py-1.5 rounded-md border text-xs font-semibold transition-all ${
                  isClosest
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                    : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400'
                }`}
              >
                <span className="font-bold leading-tight">{p}%</span>
                <span className={`text-[10px] font-mono-num leading-tight ${isClosest ? 'text-white/90' : 'text-emerald-600/80'}`}>
                  ${(projected - extra).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* -- UndoButton --------------------------------------------------------- */
/* Small pill button that pops the most recent pricing snapshot off the    */
/* in-memory history. Tooltip shows the "Was $X → Now $Y" amounts so the   */
/* appraiser can sanity-check before stepping back.                        */
function UndoButton({ history, currentGrand, currentIncGst, onUndo }) {
  const last = history.length > 0 ? history[0] : null; // newest-first
  if (!last) return null;
  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Relative timestamp e.g. "2m ago".
  const rel = (() => {
    if (!last.ts) return '';
    const diffSec = Math.max(1, Math.floor((Date.now() - new Date(last.ts).getTime()) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  })();
  return (
    <button
      type="button"
      data-testid="qs-undo-button"
      onClick={onUndo}
      title={`Undo last pricing change\nWas: ${fmt(last.grand_total)} (excl. GST)\nNow: ${fmt(currentGrand)}\nBy ${last.user_name || 'unknown'} ${rel}`}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:border-[#D35400] hover:text-[#D35400] transition-all"
    >
      <span>↶ Undo</span>
      <span className="text-[10px] font-mono-num text-slate-400">
        {fmt(last.grand_total)} → <span className="text-slate-700">{fmt(currentGrand)}</span>
      </span>
      <span className="text-[10px] text-slate-400">
        by <span className="font-semibold text-slate-600">{last.user_name || '—'}</span> {rel && <span className="text-slate-400">· {rel}</span>}
      </span>
      <span className="text-[10px] font-bold text-slate-400">({history.length})</span>
    </button>
  );
}


/* -- TargetGrandTotalInput --------------------------------------------- */
/* "I want to land at $X" reverse-solve. Accepts a target Grand Total in   */
/* either GST-exclusive or GST-inclusive terms, then computes the markup   */
/* (or extra-discount if markup would need to go below 0%) needed to hit   */
/* that figure exactly. Mirrors the math used by the Target Margin pills. */
function TargetGrandTotalInput({ summary, onApply }) {
  const [val, setVal] = useState('');
  const [includesGst, setIncludesGst] = useState(false);
  const subtotal = Number(summary.subtotal || 0);
  const discountFrac = (Number(summary.discount_pct || 0)) / 100.0;
  const gstRate = Number(summary.gst_rate || 0.15);
  const canSolve = subtotal > 0;

  const apply = () => {
    const raw = Number(val) || 0;
    if (raw <= 0) return;
    const targetExcl = includesGst ? raw / (1 + gstRate) : raw;
    onApply(targetExcl);
    setVal('');
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="qs-target-grand-row">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Target $</span>
      <input
        type="number"
        data-testid="qs-target-grand-input"
        placeholder="e.g. 1000"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
        min={0}
        step={10}
        className="w-24 text-sm font-bold text-slate-900 bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono-num focus:border-[#D35400] focus:outline-none"
      />
      <div className="flex bg-slate-100 rounded-full p-0.5 text-[10px] font-bold">
        <button
          type="button"
          data-testid="qs-target-grand-excl"
          onClick={() => setIncludesGst(false)}
          className={`px-2 py-0.5 rounded-full transition-all ${!includesGst ? 'bg-white text-[#D35400] shadow-sm' : 'text-slate-500'}`}
        >excl. GST</button>
        <button
          type="button"
          data-testid="qs-target-grand-inc"
          onClick={() => setIncludesGst(true)}
          className={`px-2 py-0.5 rounded-full transition-all ${includesGst ? 'bg-white text-[#D35400] shadow-sm' : 'text-slate-500'}`}
        >inc. GST</button>
      </div>
      <button
        type="button"
        data-testid="qs-target-grand-apply"
        disabled={!canSolve || !val || Number(val) <= 0}
        onClick={apply}
        className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#D35400] text-white border border-[#D35400] hover:bg-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >Land here</button>
    </div>
  );
}



/**
 * ServiceOptionAndSubtypePicker — twin <select>s rendered inline. The
 * first picks the Roof Service Option (Reroof · New Roof · Repairs ·
 * Restoration); the second picks the Roof Subtype (Iron · Modern
 * Concrete · Old Concrete · Clay · Decramastic · Colortile) which
 * drives defect-catalogue + paint-system lookups for the Repair /
 * Restoration builders.
 *
 * Both lists are loaded from the backend so admins can extend them
 * over time via the catalogue endpoints.
 */
function ServiceOptionAndSubtypePicker({ api, serviceValue, subtypeValue, onServiceChange, onSubtypeChange }) {
  const [opts, setOpts] = React.useState([]);
  const [subs, setSubs] = React.useState([]);
  React.useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get('/service-options'),
          api.get('/roof-subtypes'),
        ]);
        setOpts(Array.isArray(a.data) ? a.data : []);
        setSubs(Array.isArray(b.data) ? b.data : []);
      } catch (_) { /* defaults */ }
    })();
  }, [api]);
  return (
    <div className="space-y-2" data-testid="service-option-subtype-picker">
      <div data-testid="service-option-readonly" className="w-full px-3 py-2 border border-[#E2E2DF] bg-slate-50 rounded-md text-sm text-slate-700">
        {(opts.find(o => o.key === serviceValue) || {}).label || serviceValue || '—'}
        <span className="block text-[10px] text-slate-400 italic">Follows the Quotation Type — change that instead.</span>
      </div>
      {/* Roof subtype — drives defect catalogue + paint system. Optional
          but strongly recommended for Repairs / Restoration jobs. */}
      <select
        data-testid="roof-subtype-select"
        value={subtypeValue || ''}
        onChange={(e) => onSubtypeChange(e.target.value)}
        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-xs bg-white text-slate-600"
      >
        <option value="">— Roof subtype (for Repairs/Restoration) —</option>
        {subs.map((s) => (
          <option key={s.key} value={s.key}>{s.label}</option>
        ))}
      </select>
      <p className="text-[10px] text-slate-400 italic leading-tight">
        Subtype drives the defect catalogue (labour/materials rates) and
        the paint system for Restoration quotes.
      </p>
    </div>
  );
}

